// src/oura/screens/PagesWorkshop.tsx
//
// The Pages tab — the SELECTION step of the page workshop. One card: every
// crawled page in a ranked table, joined with its Search Console reality when
// the connector exists. Oura's picks lead the table, marked Chosen; "View page
// details" opens the workshop (the WORK step) for that page.
//
// The action is deliberately named for what it DOES, not what we hope it leads
// to. It opens the extracted page and whatever has been drafted for it —
// drafting itself is a separate, explicit button inside. "Improve page"
// promised an improvement the click does not perform.
//
// Deliberately ONE surface, no banners. An earlier cut stacked two blue
// explainer panels above the table; both restated what the table itself can
// say. The Search Console nudge is a single line attached to the table it
// affects, and the machine's picks are simply the rows that lead.
//
// The two flags are a CLASSIFICATION, not a score, taken from the team's own
// GSC analysis workflow — because each implies a different fix:
//
//   ranks low             high impressions at position 16 to 20. The page sits
//                         on page 2 of Google: a ranking problem.
//   ranks well, no clicks position 4 to 7 but CTR under 0.4%. People see it
//                         and skip it: a title and snippet problem.
//
// Both are pure arithmetic on GSC fields — no model call.

import { rowClick } from "@oura/ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
    AlertTriangleIcon,
    CheckCircle2Icon,
    ChevronDownIcon,
    ChevronUpIcon,
    ArrowRightIcon,
    ChevronsUpDownIcon,
    FileTextIcon,
    SearchIcon,
    SparklesIcon,
    Loader2Icon,
    XIcon,
    EyeOffIcon,
} from "lucide-react";
import type { Business, ScoreSnapshot } from "../contract";
import type { AuditIssue, BusinessAudit } from "../audit";
import { getAudit } from "../audit";
import type { AeoLinkGraph, AeoSearchPerformance, PageKind } from "@service/aeo";
import { getLinkGraph, getPageContent, getPageDraft, getPageHistory, createPageDraft, publishPageChanges, syncConnector, setPageStatus } from "@service/aeo";
import type { AeoPagePublishResult } from "@service/aeo";
import { samplePagePublish, samplePasteOnly } from "@service/aeo/publishRefusal";
import { nyonyaSamplePage } from "../publish/samplePage";
import { ASK_OURA_ON, PlanYesDialog, planProposals, sayYesInChat, usePlanAnswers, type YesChange } from "@oura/ask";
import { useSignedInName } from "../docs/signedIn";
import { adaptPageWorkshop, linkGraphHealth } from "../livePageWorkshop";
import type { GscState } from "../gscState";
import type { View } from "../nav";
import { cn } from "../lib/cn";
import { pagePath } from "../lib/oura";
import { Card, CardBody, CardHeader } from "../primitives/Card";
import { EmptyState } from "../primitives/EmptyState";
import { Pagination } from "../primitives/Pagination";
import { SegmentedControl } from "../primitives/SegmentedControl";
import { PageIdentity } from "../components/PageIdentity";
import { PageWorkshopView } from "../components/PageWorkshopView";
import {
    roleForUrl,
    isSetAsideRole,
    COPY_WORK_ROLES,
    ROLE_HINT,
    ROLE_LABEL,
    SET_ASIDE_ROLES,
    EMPTY_REVIEW,
    type PageReview,
    type PageRole,
    type PageSearchMetrics,
    type PageWorkshop,
} from "../pageWorkshop";
import { fetchStoredRoles, loadStoredRoles, persistRole, storeRoles } from "../pageRoles";
import { Input } from "@component/input";
import { Button } from "../primitives/Button";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@component/select";

/** 15, not the house default of 20 (skill §7): this table now carries the whole
 *  crawl rather than a four-row shortlist, and each row is two lines tall with
 *  a Role select in it, so 20 overran the viewport before the pager appeared. */
const PAGE_SIZE = 15;
const WORKING_SET_SIZE = 5;

/**
 * Reviewer notes and the staged set survive a reload, per business.
 *
 * They were browser-only component state, so a reload threw away every note a
 * reviewer had written across every page — work the product had visibly
 * counted for them. The BE stores notes only when a redraft is fired (they
 * compile into the draft's instructions), so between writing and redrafting
 * the browser is the only place they exist; localStorage is that place.
 *
 * The staged set (#3546) rides along in the same record. Persisting it is
 * safe in a way persisting dismissals was not: a restored staged set is
 * spelled out in the staged-set bar and counted on the Publish button, so a
 * reviewer returning to the page can SEE what a publish would write. Whether
 * it should ALSO persist server-side is an open decision, and keeping it
 * local loses nothing while that is made.
 */
const reviewStorageKey = (businessId: string) => `oura:page-reviews:${businessId}`;

function loadStoredReviews(businessId: string | undefined): Record<string, PageReview> {
    if (!businessId) return {};
    try {
        const raw = localStorage.getItem(reviewStorageKey(businessId));
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        // Rebuilt field by field rather than cast: a stale or hand-edited
        // record must degrade to an empty review, not crash the tab render.
        const out: Record<string, PageReview> = {};
        for (const [url, r] of Object.entries(parsed as Record<string, unknown>)) {
            if (!r || typeof r !== "object") continue;
            // A stored `dismissed` key is deliberately not read (#3546). It was
            // written under inclusion-by-default, where an empty list meant
            // "publish everything"; under staging it would have to mean the
            // opposite. Dropping it opens the page with nothing staged, which is
            // the safe reading of either.
            const rev = r as { staged?: unknown; notes?: unknown };
            out[url] = {
                staged: Array.isArray(rev.staged)
                    ? rev.staged.filter((x): x is string => typeof x === "string")
                    : [],
                notes:
                    rev.notes && typeof rev.notes === "object" && !Array.isArray(rev.notes)
                        ? (Object.fromEntries(
                            Object.entries(rev.notes as Record<string, unknown>).filter(
                                ([, v]) => typeof v === "string",
                            ),
                        ) as Record<string, string>)
                        : {},
            };
        }
        return out;
    } catch {
        return {};
    }
}

type Flag = "ranks_low" | "no_clicks" | null;

const FLAG_LABEL: Record<Exclude<Flag, null>, string> = {
    ranks_low: "Ranks low",
    no_clicks: "Ranks well, not clicked",
};

/**
 * Crawl findings in words a reviewer can act on, not crawler jargon. They
 * share ONE column with the GSC flags ("Issues") — a diagnosis is a
 * diagnosis, whichever instrument produced it — and render as plain muted
 * text, because a crawl of a thin site would otherwise paint a wall of
 * identical amber chips that shout without informing.
 */
const FINDING_LABEL: Record<string, string> = {
    "Orphan page": "No other page links here",
    "Thin content": "Very little copy on the page",
    "No internal links": "Links to no other page",
    "404": "Page not found (404)",
};

/**
 * Media and asset URLs are NOT pages and never belong in this table — there is
 * no copy on a jpg to improve, and a machine "choosing" a broken image as a
 * top-five page is exactly the kind of confident nonsense that kills trust in
 * the pre-selection. The BE stopped counting images as orphan pages
 * (ourteam-backend#3954), but the underperforming list can still carry media
 * 404s, so the FE guards regardless of which backend produced the data.
 */
const ASSET_URL = /\.(jpe?g|png|gif|webp|svg|ico|pdf|mp4|webm|css|js|woff2?)([?#]|$)/i;
function isAssetUrl(url: string): boolean {
    return ASSET_URL.test(url) || /\/wp-content\/uploads\//i.test(url);
}

/**
 * Kinds the backend generates rather than someone writing them
 * (ourteam-backend#4097): `/category/uncategorized`, `/author/abinai_8f7wo4`,
 * `/page/3`, feeds and on-site search results.
 *
 * These get the `utility` role by default so they stay out of copy work, which
 * is what #4097 actually asked for. They are NOT dropped from the table.
 *
 * `placeholder` and `unknown` are deliberately absent from this list. Both are
 * real pages at real URLs — demo copy nobody replaced, and pages the crawl
 * could not read — and the audit reports findings against them. Excluding them
 * from the table while the findings still named them is what produced
 * "/services/orthodontics is under Duplicate title tags but not in Crawled
 * Pages", and left the page unreachable, so a reviewer could not even mark it
 * out of scope.
 */
const GENERATED_INDEX_KINDS: PageKind[] = ["taxonomy", "archive", "paginated", "feed", "search"];

function isGeneratedIndex(kind: PageKind | undefined): boolean {
    return kind !== undefined && GENERATED_INDEX_KINDS.includes(kind);
}

/**
 * A date-stamped permalink that the backend mislabelled `archive`.
 *
 * `page_kind()` classifies by URL, and its archive pattern
 * (`/(19|20)\d{2}(/\d{1,2})?(/|$)`) matches the DATE PREFIX rather than a
 * date-only path — so on every WordPress site using the default permalink
 * structure, `/2017/03/23/dental-insurance` is filed as a generated index. That
 * made `isIndexPage` drop those posts from this table while the Site Audit tab
 * went on listing them under Thin content, which is exactly the mismatch a
 * reviewer sees as "pages in Issues that are not in Crawled Pages" — and it
 * left the page unreachable, so it could not even be labelled Placeholder.
 *
 * Corrected here rather than waited on, because the backend fix has to ship,
 * be deployed and be re-crawled before a single existing scan changes. Narrow
 * on purpose: only `archive`, only when the path carries a slug AFTER the
 * date, so a genuine `/2017/03/` listing and every `/archives/` page keep the
 * classification they were given.
 *
 * REMOVE once the backend pattern is anchored — see the handoff note.
 */
const DATED_POST = /\/(19|20)\d{2}(\/\d{1,2}){0,2}\/[^/]*[a-z][^/]*\/?$/i;

function correctedKind(url: string, kind: PageKind | undefined): PageKind | undefined {
    if (kind !== "archive") return kind;
    return DATED_POST.test(pagePath(url)) ? "content" : kind;
}

/**
 * The identity of a page, for comparing two lists that spell it differently.
 *
 * Scheme, a `www.` prefix and a trailing slash carry no meaning for "is this
 * the same page", but every source here disagrees about them: the crawl
 * returns absolute URLs, the link graph returns bare paths, and Search Console
 * returns whichever form the property was registered under. Comparing raw
 * strings silently produces "no match", which reads on screen as "no data" —
 * the same failure we asked the backend to fix on its own GSC join.
 */
/**
 * Where a page sits in the site, for reading the table as a structure.
 *
 * `depth` is the number of path segments, so the home page (none) always sorts
 * first, then top-level pages, then their children. `sort` is the lowercased
 * path, which puts siblings in a stable alphabetical run underneath the parent
 * they belong to.
 *
 * Ranking by findings instead made the list unreadable as an inventory: the
 * home page could appear two thirds of the way down, and there was no way to
 * tell a section from a stray page. A crawl listing is a map before it is a
 * worklist.
 */
function hierarchyKey(url: string): { depth: number; sort: string } {
    let path = url;
    try {
        path = new URL(url.startsWith("http") ? url : `https://${url}`).pathname;
    } catch {
        // Keep the raw string — splitting on "/" still yields usable segments.
    }
    const segments = path.split("/").filter(Boolean);
    return { depth: segments.length, sort: segments.join("/").toLowerCase() };
}

function matchKey(url: string): string {
    return url
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/+$/, "")
        .toLowerCase();
}

export interface PageRow {
    url: string;
    title: string | undefined;
    clicks: number | null;
    impressions: number | null;
    ctr: number | null;
    position: number | null;
    flag: Flag;
    finding: string | null; // crawl-side reason (Orphan page / Thin content / 404)
    chosen: boolean;
    /**
     * Crawl-inventory context, joined from the link graph. All three are
     * optional because a row can exist without the graph knowing it.
     */
    /** Internal links pointing here from page COPY (not nav/header/footer). */
    inboundInBody?: number;
    /** The graph reached this page and nothing links to it. */
    orphan?: boolean;
    /** The assessment never flagged this page — it is here because the crawl
     *  read it. Rows are ordered by site structure, not by this, so it only
     *  separates the flagged count in the header from the inventory. */
    inventoryOnly?: boolean;
    /**
     * Every Site Audit check this page failed (ourteam-backend#3957), as
     * `{ key, label }`.
     *
     * This is the page's full problem list — all eleven checks — where `finding`
     * above can only ever say one of three things, because it comes from the
     * BE's `underperforming[]` summary (404 · Orphan page · Thin content, capped
     * at twelve rows). Both are kept: `finding` is what the ranking used to pick
     * this row, `issues` is what is actually wrong with the page.
     *
     * Absent = this run reported no crawl inventory. Empty = the crawler read
     * the page and it passed everything, which is a result worth stating.
     */
    issues?: Array<{ key: string; label: string }>;
    /**
     * The crawler read this page and it has no <title> at all.
     *
     * Only ever set from the crawl inventory, never inferred from the name
     * being blank in this table — those are different claims. A name can be
     * blank because no source in this payload happened to carry one; a title
     * is MISSING only when something actually looked at the page and found
     * none. Saying the second when we only know the first is how a reporting
     * tool starts inventing defects.
     */
    missingTitle?: boolean;
}

/**
 * `searchPerformance` carries CTR as a FRACTION, not a percentage.
 *
 * The wire type does not say so (`AeoSearchPage.ctr: number`), but the shipped
 * Search Performance screen settles it: `pct()` there renders every CTR as
 * `(value * 100).toFixed(1)%`, and that screen was built against real Search
 * Console data. Comparing a fraction straight against the team's 0.4% rule
 * meant testing `0.012 < 0.4` — true for any page under 40% CTR, i.e. very
 * nearly all of them — so the "ranks well, no clicks" flag fired almost
 * everywhere and dragged the wrong pages into Oura's chosen five.
 *
 * Converted here rather than at the adapter so the raw contract value stays
 * untouched, and defensively: a value above 1 is already a percentage.
 */
function ctrPercent(ctr: number): number {
    return ctr <= 1 ? ctr * 100 : ctr;
}

/**
 * Average position, as a whole number.
 *
 * The raw figure is an average over impressions, so it arrives as 6.15, 8.67,
 * 8.82 — a column of numbers with different decimal lengths that no longer
 * lines up and reads as more precise than it is. Nobody acts on the difference
 * between 8.67 and 8.82; they act on "about 9".
 *
 * Rounded for DISPLAY only. The sort still runs on the raw value, so two pages
 * that both show 9 keep their real order, and the exact figure stays on the
 * cell's tooltip — this makes the column readable without discarding what it
 * measured.
 */
function positionLabel(position: number): string {
    return String(Math.round(position));
}

/**
 * The two deterministic classifications. Thresholds are the team's own, from
 * their GSC analysis: hypotheses to validate with real usage, not facts — but
 * at least THEIR hypotheses, not invented here.
 */
function classify(impressions: number, ctr: number, position: number): Flag {
    if (impressions >= 500 && position >= 15.5 && position <= 20.5) return "ranks_low";
    if (impressions >= 500 && position >= 3.5 && position <= 7.5 && ctrPercent(ctr) < 0.4)
        return "no_clicks";
    return null;
}

/**
 * The healthy verdict, as deterministic as the flags: first-page position AND
 * actual clicks, with nothing wrong on the crawl side. A page with search
 * history that clears neither bar gets "—" — no verdict, not a clean bill.
 */
function isHealthy(r: PageRow): boolean {
    return (
        r.clicks !== null &&
        r.clicks > 0 &&
        r.position !== null &&
        r.position <= 10
    );
}

function fmt(n: number | null): string {
    return n === null ? "—" : n.toLocaleString("en-GB");
}

/**
 * Which slice of the crawl the table is showing.
 *
 * The default is NOT the whole crawl. A reviewer opening this tab is looking
 * for the next page to improve, and on a shop or a site with a drawer full of
 * unpublished stubs, a third of the inventory is neither — so the whole crawl
 * as the opening view makes them re-filter it by eye every single time.
 *
 *   all    "All pages" — the full crawl, unfiltered. Listed FIRST because it is
 *          the superset the other two are carved out of; between them it read
 *          as a third, unrelated view.
 *   work   "To work on" — what copy work applies to. Everything except the
 *          set-aside roles. The default: the tab order states the hierarchy,
 *          the default states the job.
 *   aside  "Out of scope" — ONLY the set-aside roles: the product catalogue
 *          that needs no copy, and the placeholder pages whose fix is deletion.
 *          A worklist of its own — the list you hand someone to clean up the
 *          site.
 *
 * Named "Out of scope" rather than "Archived" deliberately: archiving describes
 * something that has been put away, and a product page is live, published and
 * selling. What these pages have in common is not their state on the site, it
 * is that OUR work does not apply to them.
 */
type TableView = "work" | "all" | "aside";

/**
 * The tabs, and what each one contains — the ATS applicants board's pattern
 * (`employer-applicants-list.tsx`), where the selected tab states its own scope
 * in a line directly beneath the bar.
 *
 * The copy lives in this table rather than in a chain of ternaries in the JSX
 * (which is what the ATS does, and which has already drifted there: its
 * "Ready for Review" tab is described as "Ready to Review"). A tab and its
 * description that cannot be read side by side will eventually disagree.
 *
 * Each description absorbs the claim the old footnote used to make at the very
 * bottom of the card, where nobody looked for it: the read-only promise belongs
 * with All pages, and the browser-only caveat belongs with the tab whose two
 * roles it is actually about.
 */
const VIEW_TABS: { value: TableView; label: string; description: string }[] = [
    {
        value: "all",
        label: "All pages",
        description:
            "Every page Oura read on this site. Opening one reads the live page and shows any proposals drafted for it — nothing is ever written to the site.",
    },
    {
        value: "work",
        label: "To work on",
        // Utility was missing from this sentence — it shipped after the line was
        // written, so the tab named two of the three roles that actually move a
        // page out of this list and stayed silent about the third.
        description:
            "Pages your copy work applies to. Anything labelled Product, Utility or Placeholder is held in Out of scope, and Oura does not pick those pages.",
    },
    {
        value: "aside",
        label: "Out of scope",
        description:
            "Product and utility pages need no copy proposals; placeholder pages should be removed from the site. Contact and outlets pages are deliberately not here — they answer the location questions Oura asks. These labels are shared with your team.",
    },
];

type SortKey = "site" | "clicks" | "impressions" | "ctr" | "position";
type SortDir = "asc" | "desc";

/**
 * Which way a column sorts on FIRST click — the useful way, not alphabetical
 * habit. Clicks, impressions and CTR are "more is better", so they open
 * biggest-first. Position is a rank: 1 is the best result on the page, so it
 * opens smallest-first. A column that opened the wrong way every time would
 * cost two clicks to read the answer everyone actually wants.
 */
const SORT_DEFAULT_DIR: Record<Exclude<SortKey, "site">, SortDir> = {
    clicks: "desc",
    impressions: "desc",
    ctr: "desc",
    position: "asc",
};

/**
 * Compare one metric, with **nulls last in both directions**.
 *
 * A null here means Search Console reported nothing for the page, which is not
 * a zero and must not sort like one: ascending by position would otherwise open
 * on a screen of dashes, and the reviewer would conclude the sort is broken
 * rather than that those pages have no search history.
 */
/** The sortable value behind a column. CTR is normalised through
 *  `ctrPercent` so a fraction and a percentage cannot end up interleaved. */
function metricOf(r: PageRow, key: SortKey): number | null {
    switch (key) {
        case "clicks":
            return r.clicks;
        case "impressions":
            return r.impressions;
        case "position":
            return r.position;
        case "ctr":
            return r.ctr === null ? null : ctrPercent(r.ctr);
        default:
            return null;
    }
}

function compareMetric(a: number | null, b: number | null, dir: SortDir): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return dir === "desc" ? b - a : a - b;
}

/**
 * The diagnosis for one row: a short label, and what it means.
 *
 * The order is the RANKING'S order, not a second opinion: the sort weighs a GSC
 * flag above a crawl finding, so the label leads with whichever one actually
 * carried the row. Anything else would describe a decision we did not make.
 */
/**
 * One diagnosis on a page: whether it is good news, and what it says.
 *
 * A LIST, not a single verdict. A page can rank on page 2 *and* be an orphan,
 * and the old cell showed only the first of those — the ranking's own priority
 * order leaked into the display and quietly hid the second finding. This shape
 * also takes the checks the team has asked for next (unstructured data, missing
 * meta description, 404s) without another rewrite of the column: they are
 * additional entries, not additional branches.
 */
interface RowFinding {
    tone: "good" | "warn" | "muted";
    label: string;
}

/**
 * Take a placeholder page off the live site (ourteam-backend#4529 §3).
 *
 * The one destructive write in this table, so it gets a step of its own rather
 * than a menu item that fires on click. Two things are stated before the
 * confirm, because both decide whether this is the right button:
 *
 *   - WHAT happens. Moving to draft is reversible in WordPress; the client's
 *     editors can put the page back. That is why it is the only action offered
 *     here — Trash exists on the API and is deliberately not surfaced.
 *   - WHICH page. A URL, in full, not a title: two placeholder pages from the
 *     same site template routinely carry the same title.
 *
 * The backend refuses anything that is not already classed `placeholder`, and
 * refuses it as a 409 whose message says which check failed. That message is
 * shown verbatim rather than translated: "the last crawl did not reach it" and
 * "a reviewer labelled it product" call for different next steps, and a generic
 * failure hides both.
 */
function RemovePageDialog({
    url,
    title,
    busy,
    error,
    onConfirm,
    onClose,
}: {
    url: string;
    title: string;
    busy: boolean;
    error: string | null;
    onConfirm: () => void;
    onClose: () => void;
}) {
    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold leading-snug text-gray-900">Take this page off the site</h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            It moves to draft in WordPress. Visitors and search engines stop seeing it,
                            and your client can publish it again at any time.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <div className="truncate text-sm font-medium text-gray-900">{title}</div>
                    <div className="mt-0.5 break-all text-xs text-gray-500">{url}</div>
                </div>

                {error && (
                    <p className="mt-3 flex items-start gap-1.5 text-sm leading-relaxed text-rose-700">
                        <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span>{error}</span>
                    </p>
                )}

                <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={onConfirm} disabled={busy}>
                        {busy && <Loader2Icon className="h-4 w-4 animate-spin" />}
                        Move to draft
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/** The "no verdict either way" placeholder. A named constant because two
 *  things now depend on recognising it: the cell that renders it, and the
 *  container that centres itself around it. */
const EMPTY_FINDING = "—";

function findingsFor(r: PageRow, role: PageRole): RowFinding[] {
    // A set-aside role OUTRANKS every diagnosis, because it contradicts them. A
    // placeholder page can rank low and a product page can go unclicked — both
    // are true, and both point the reviewer at work that should not be done on
    // that page. The label a reviewer applied is the more recent, better
    // informed reading, so it replaces the column rather than joining it.
    if (role === "placeholder") return [{ tone: "muted", label: "Should be removed" }];
    if (role === "product" || role === "utility") {
        return [{ tone: "muted", label: "Not copy work" }];
    }

    const findings: RowFinding[] = [];
    // GSC first, then the crawl: a search-behaviour problem is the one the
    // reviewer can act on with copy, which is the work this tab leads to.
    if (r.flag) findings.push({ tone: "warn", label: FLAG_LABEL[r.flag] });
    if (r.finding) {
        findings.push({ tone: "warn", label: FINDING_LABEL[r.finding] ?? r.finding });
    }

    // Why this row shows an address where its neighbours show a name. Placed
    // FIRST among the crawl findings because it is the question a reader asks
    // about the row before any of the others — and it belongs here, in the
    // column that explains rows, rather than as a placeholder sitting where the
    // page's name should be. It is also not "missing meta description", which
    // is a separate tag with its own check listed further down this same cell.
    if (r.missingTitle) {
        findings.push({ tone: "warn", label: "Missing page title" });
    }

    // Then the Site Audit's own findings for this page.
    //
    // `finding` above can only ever be one of three things — it comes from the
    // BE's `underperforming[]` summary, which is built from three of the eleven
    // checks. So a page with no meta description, no structured data and a
    // duplicate title read as having NOTHING wrong with it here, while the Site
    // Audit tab listed it three times. Same crawl, two answers.
    //
    // De-duped against what is already shown: `underperforming` is derived from
    // these very checks, so a 404 or an orphan would otherwise appear twice
    // under two different names.
    const shown = new Set(findings.map((f) => f.label.toLowerCase()));
    (r.issues ?? []).forEach((iss) => {
        if (shown.has(iss.label.toLowerCase())) return;
        shown.add(iss.label.toLowerCase());
        findings.push({ tone: "warn", label: iss.label });
    });

    if (findings.length > 0) return findings;

    // The positive verdict, stated — a page that ranks and earns clicks should
    // SAY it needs nothing, rather than leave a dash the reviewer interprets.
    if (isHealthy(r)) return [{ tone: "good", label: "Performing well" }];
    // A crawled page with an empty problem list passed every check. That is a
    // measurement, not an absence, and it is only sayable now that the list
    // exists — before, "nothing wrong" and "nothing checked" were the same "—".
    if (r.issues?.length === 0) return [{ tone: "good", label: "No issues found" }];
    // Still not a clean bill: no verdict either way. The dash is the honest
    // answer for a page with no search history and nothing wrong on the crawl.
    return [{ tone: "muted", label: EMPTY_FINDING }];
}

/**
 * Is this row's only "finding" the em dash — i.e. nothing to report?
 *
 * Asked by the cell's CONTAINER, which has to decide how to align before it
 * knows what is inside. Goes through `findingsFor` rather than re-deriving the
 * condition, so "nothing to say" can never mean one thing to the alignment and
 * another to the text.
 */
function hasNoFindings(row: PageRow, role: PageRole): boolean {
    const findings = findingsFor(row, role);
    return findings.length === 1 && findings[0].label === EMPTY_FINDING;
}

/**
 * The "Chosen by Oura" mark. Same Oura Insights label treatment
 * as everywhere else — this is the same "Oura says" voice, so
 * it wears the same face.
 *
 * Deliberately carries NO tooltip. The reason lives in the "Issues" cell
 * on the same row and the evidence lives in the four metric columns before it,
 * so a hover here would hide behind a gesture what the row already states, and
 * duplicate the numbers sitting inches to the left.
 */
export function ChosenBadge() {
    return (
        <span className="inline-flex flex-shrink-0 items-center gap-1">
            <SparklesIcon className="h-3 w-3 text-blue-600" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">
                Chosen by Oura
            </span>
        </span>
    );
}

/** One finding's row: icon + label, coloured by tone. */
function FindingRow({ finding }: { finding: RowFinding }) {
    return (
        <div
            className={cn(
                "flex items-center gap-1.5 text-xs font-medium",
                finding.tone === "good"
                    ? "text-green-700"
                    : finding.tone === "warn"
                        ? "text-amber-700"
                        : "text-gray-500",
            )}
        >
            {/* Muted rows carry no glyph — an em dash is the ABSENCE of a
                finding, and giving it an icon would dress "we do not know" up as
                a result. */}
            {finding.tone === "good" ? (
                <CheckCircle2Icon className="h-3.5 w-3.5 flex-shrink-0" />
            ) : finding.tone === "warn" ? (
                <AlertTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
            ) : null}
            <span>{finding.label}</span>
        </div>
    );
}

/**
 * The "Issues" cell: what is wrong with this page, folded up.
 *
 * FOLDED BY DEFAULT, which is the whole point. Now that a row lists every check
 * a page failed rather than the three the old summary carried, a busy site put
 * six amber lines against every row and the table became a wall of warnings —
 * the same words repeating down the page, none of them readable at a glance and
 * the metric columns pushed apart by rows three times taller than their content.
 * Collapsed, each row states its SIZE ("6 issues identified") and the reviewer
 * opens the one they are working on. Same disclosure as the Insights column on
 * the Recruiter board, and the same chevron, because it is the same gesture.
 *
 * A VERDICT IS NOT A LIST. "Performing well", "No issues found", "Not copy
 * work", "Should be removed" and the em dash are single statements about the
 * page, not things to count — so they render as they always did, with no
 * chevron and nothing to open. Folding a one-line verdict behind "1 issue
 * identified" would hide a shorter string behind a longer one, and would call a
 * clean bill of health an issue.
 */
export function IssuesCell({ row, role }: { row: PageRow; role?: PageRole }) {
    // Declared before the early return: hooks cannot be conditional.
    const [collapsed, setCollapsed] = useState(true);
    const resolvedRole = role ?? roleForUrl(row.url);
    const findings = findingsFor(row, resolvedRole);
    // Only real problems are counted. A "good" or "muted" line is a verdict.
    const issues = findings.filter((f) => f.tone === "warn");

    if (issues.length === 0) {
        return (
            <div className={cn("max-w-[420px] space-y-1", hasNoFindings(row, resolvedRole) && "flex justify-center")}>
                {findings.map((f) => (
                    <FindingRow key={f.label} finding={f} />
                ))}
            </div>
        );
    }

    return (
        // `items-center` when it is one line, `items-start` when it is a list.
        // Fixed `items-start` left the chevron riding above the summary text:
        // the button carries its own padding, so aligning their top edges puts
        // the glyph a few pixels higher than the words next to it, which at this
        // size reads as a misalignment rather than as a deliberate offset.
        <div
            className={cn(
                "flex max-w-[420px] gap-1.5",
                collapsed ? "items-center" : "items-start",
            )}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setCollapsed((c) => !c);
                }}
                aria-expanded={!collapsed}
                aria-label={collapsed ? "Show issues" : "Hide issues"}
                className="-ml-1 flex-shrink-0 rounded p-1 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
            >
                <ChevronDownIcon
                    className={cn(
                        "h-4 w-4 text-gray-500 transition-transform",
                        collapsed && "-rotate-90",
                    )}
                />
            </button>
            <div className="min-w-0 flex-1">
                {collapsed ? (
                    // Muted, not amber. Collapsed, this line is a COUNT, and a
                    // column of amber counts would read as six separate alarms
                    // rather than as the summary of one page each.
                    <span className="text-xs text-gray-500">
                        {issues.length} {issues.length === 1 ? "issue" : "issues"} identified
                    </span>
                ) : (
                    <div className="space-y-1">
                        {findings.map((f) => (
                            <FindingRow key={f.label} finding={f} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// House style §4: the header row is a fixed 45px so every table in the product
// has the same top edge, whatever its label lengths.
const TH = "h-[45px] px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap";
/** The action column, held at the table's right edge with a soft edge over
 *  the columns scrolling under it. Each cell adds its own ground. */
const STICKY_ACTION = "sticky right-0 z-[1] shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]";

/** A metric cell. `—` is `gray-400`, a shade lighter than a real number, so a
 *  column of unmeasured pages reads as absent rather than as data (§4). */
function MetricCell({ children, empty }: { children: ReactNode; empty: boolean }) {
    return (
        <td
            className={cn(
                // Every cell in the row is middle-aligned — the table's default,
                // and now the honest one. The ragged column came from a
                // top-aligned findings cell carrying two lines of explanation
                // next to centred numbers; with the explanations gone, no cell
                // is tall enough for top alignment to buy anything.
                "whitespace-nowrap px-4 py-4 text-center text-sm tabular-nums",
                empty ? "text-gray-400" : "text-gray-700",
            )}
        >
            {empty ? "—" : children}
        </td>
    );
}

/**
 * A sortable column header (house style §4): the label, and a chevron that says
 * which way it is pointing. Inactive columns keep a faint two-way chevron so
 * the column advertises that it CAN be sorted — a header that only reveals
 * itself on hover is a feature nobody finds.
 */
function SortHeader({
    label,
    sortKey,
    sort,
    onSort,
    align = "center",
    thClassName,
}: {
    label: string;
    sortKey: SortKey;
    sort: { key: SortKey; dir: SortDir };
    onSort: (key: SortKey) => void;
    align?: "left" | "center";
    /** Width control for a column that needs one — see the Page header. */
    thClassName?: string;
}) {
    const active = sort.key === sortKey;
    return (
        <th
            className={cn(TH, align === "left" ? "text-left" : "text-center", thClassName)}
            aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        >
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={cn(
                    "flex w-full cursor-pointer select-none items-center gap-1 uppercase tracking-wider hover:text-gray-800",
                    align === "left" ? "justify-start" : "justify-center",
                )}
            >
                {label}
                {active ? (
                    sort.dir === "asc" ? (
                        <ChevronUpIcon className="h-3 w-3" />
                    ) : (
                        <ChevronDownIcon className="h-3 w-3" />
                    )
                ) : (
                    <ChevronsUpDownIcon className="h-3 w-3 opacity-40" />
                )}
            </button>
        </th>
    );
}

/**
 * What the link engine had to work with, as ONE clause on the header line.
 *
 * Every `link` and `repoint` proposal comes out of the crawl's link graph, so
 * a page that offers one suggestion against a recommended six is reporting
 * either a thin graph or an engine that declined. Without this figure the two
 * are indistinguishable on screen and the number reads as a verdict on the
 * page.
 *
 * Only in-body links count as candidates: a page reached through the site menu
 * is not linked to in any sense a reader would recognise, which is why
 * `in_body` exists on the edge (#4099).
 *
 * `null` for every unhealthy state — those are findings, and a finding does not
 * belong in a run of neutral counts. See `linkGraphFinding`.
 */
function linkGraphClause(graph: ReturnType<typeof linkGraphHealth>): string | null {
    if (graph.state !== "ok") return null;
    return `${graph.inBodyEdges.toLocaleString("en-GB")} of ${graph.edges.toLocaleString("en-GB")} internal links in page copy`;
}

/**
 * The link graph when there is nothing to build a suggestion FROM — a finding,
 * in a sentence, or `null` when the graph is fine.
 *
 * "All navigation" is called out as its own state rather than folded into a
 * total, because a site with 200 menu links and no in-copy links is not a site
 * with 200 candidates.
 */
/**
 * What to say about a crawl that stopped early — and why it has to name the
 * REASON rather than assume one.
 *
 * This was a single hardcoded sentence about a "scan limit". That was accurate
 * while the page ceiling was what stopped every crawl. `ourteam-backend#4569`
 * raised the ceiling from 30 to 600, and the first run under it read 542 of 548
 * pages and stopped on the TIME budget instead — so the sentence named a cause
 * that no longer applied and called a six-page gap "part of the site rather
 * than all of it". Raising a limit silently falsified copy nobody had touched,
 * and it pointed the reader at the wrong lever: someone reading "scan limit"
 * goes asking for a higher page ceiling that had already been raised.
 *
 * The gap comes from `pagesKnown - pagesCrawled`, NOT `truncation.unvisited`,
 * for the same reason the header count does (see `known` above): unvisited
 * counts the live crawl queue and moves between runs, while `pagesKnown` is the
 * one stable denominator. Both report 6 on the run above; only one keeps
 * reporting it.
 *
 * The consequence clause deliberately says nothing about proportion. "Most of
 * the site" would be a guess — a time budget can expire six pages short or three
 * hundred, and the same sentence has to stay true at both ends.
 */
function crawlTruncationFinding(
    truncated: boolean | undefined,
    reason: "max_pages" | "time_budget" | undefined,
    crawled: number,
    known: number | null,
): string | null {
    if (!truncated) return null;
    const gap = known !== null && known > crawled ? known - crawled : null;
    // No `pagesKnown` on older runs and fixtures, so the size of the gap is
    // genuinely unknown there — say that rather than print a number we do not have.
    const unread = gap === null
        ? "some pages unread"
        : `${gap.toLocaleString("en-GB")} page${gap === 1 ? "" : "s"} unread`;
    const cause = reason === "time_budget"
        ? "The crawl ran out of time"
        : "The crawl stopped at its page limit";
    return `${cause} with ${unread}, so the counts above cover what it read, not the whole site.`;
}

function linkGraphFinding(
    graph: ReturnType<typeof linkGraphHealth>,
    linkGraph: AeoLinkGraph | null,
): string | null {
    switch (graph.state) {
        case "absent":
            return "Oura has no internal link graph for this site, so any link suggestion on these pages was made without one.";
        case "empty":
            return "The crawl recorded no internal links at all, so the link engine has nothing to build a suggestion from. That is a crawl finding, not a site without links.";
        case "nav_only":
            return `All ${graph.edges.toLocaleString("en-GB")} internal links found are navigation. Oura only proposes links inside page copy, so there are no candidates on this site yet.`;
        default:
            return linkGraph?.truncated === true
                ? "The link graph was truncated, so the links above are a partial view of the site."
                : null;
    }
}

/**
 * The one band on this card that carries findings.
 *
 * There used to be a band per subsystem — one for the link graph, one
 * explaining the current tab — each with its own background and its own text
 * size, stacked above a table that was already carrying a header line and a
 * footnote. Four blocks of prose in three sizes is not four times the
 * information; it is a wall a reader skips, which costs the one or two
 * sentences that actually needed reading.
 *
 * So: neutral counts live on the header line, and anything that qualifies how
 * the table should be READ collects here, in one amber band, one size.
 */
function CrawlFindings({ notes }: { notes: string[] }) {
    if (notes.length === 0) return null;
    return (
        <div className="border-b border-gray-100 bg-amber-50/50 px-4 py-2.5">
            {notes.map((note) => (
                <p key={note} className="my-0 text-xs leading-relaxed text-gray-600">
                    {note}
                </p>
            ))}
        </div>
    );
}

export function PagesWorkshop({
    snapshot,
    business,
    liveAudit,
    liveSearch,
    gscState,
    businessId,
    workshopEnabled,
    publishBlockedReason,
    publishUnsupported,
    publishTargetLabel,
    onNavigate,
    onRefreshData,
}: {
    snapshot: ScoreSnapshot;
    business: Business;
    liveAudit?: BusinessAudit;
    /** `results.searchPerformance` — undefined for a fixture business. */
    liveSearch?: AeoSearchPerformance;
    /** The reconciled Search Console state (gscState.ts), so the nudge below
     *  the table can tell "never connected" from "connected, matched nothing".
     *  Undefined on a fixture business. */
    gscState?: GscState;
    /** Live business id; absent on a fixture. */
    businessId?: string;
    /**
     * May the per-page WORKSHOP be opened at all?
     *
     * Passed explicitly by the caller rather than inferred from `businessId`
     * so the policy is visible at the mount point, where anyone auditing which
     * screens are exposed will look first. False closes every route into the
     * workshop: the row action, the `?page=` deep link and any future entry.
     * The crawled-pages table itself is real data and always renders.
     */
    workshopEnabled: boolean;
    /** Why publishing a page's changes is unavailable — no connected website
     *  platform. Passed straight to the workshop view, which shows it against a
     *  disabled Publish rather than hiding the control. Undefined = available. */
    publishBlockedReason?: string;
    /** See DraftRow — threaded through unchanged (#3692). */
    publishUnsupported?: (draftType: string) => string | undefined;
    /** The connected platform a publish writes to, by its own name
     *  ("WordPress", "Framer"). Empty when nothing is connected. */
    publishTargetLabel?: string;
    onNavigate: (v: View) => void;
    /**
     * Re-fetch the business detail AND the connector catalog, after the nudge's
     * on-demand Search Console sync. The metric columns on this table come from
     * `results.searchPerformance` on the business detail payload — a sync that
     * did not re-fetch it would leave the dashes it was pressed to fix.
     * Absent on a fixture business, where there is nothing to sync.
     */
    onRefreshData?: () => void;
}) {
    const audit = liveAudit ?? getAudit(business, snapshot);
    const gscConnected = !!liveSearch?.connected && (liveSearch.pages?.length ?? 0) > 0;

    const [page, setPage] = useState(1);

    /**
     * The nudge's on-demand Search Console pull (the new
     * `POST /connectors/gsc/sync`). Placed here as well as on the connector
     * card because this table's dashes are where an operator actually notices
     * the data is stale — sending them to another tab to fix it loses half of
     * them on the way.
     */
    const [gscSyncing, setGscSyncing] = useState(false);
    /** What the last sync said. A failed pull is a 200 with `action_needed` +
     *  a human-readable detail — an answer to render, not an error to toast.
     *  Carries a tone because a success and a failed pull both land here and
     *  must not be painted the same colour. There is no snackbar on this
     *  screen, so this line is the only feedback the button has. */
    const [gscSyncNote, setGscSyncNote] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);
    const refreshSearchConsole = async () => {
        if (!businessId || gscSyncing) return;
        setGscSyncing(true);
        const result = await syncConnector(businessId, "gsc");
        setGscSyncing(false);
        if (!result.ok) {
            setGscSyncNote({ tone: "warn", text: result.message });
            return;
        }
        if (result.connector.status === "connected") {
            setGscSyncNote({ tone: "ok", text: "Search Console re-read just now." });
        } else {
            // An unrecognised 200 reaches us as `action_needed` with an empty
            // detail (see services/aeo). Guarding the note on a non-empty
            // detail meant that case rendered nothing and the button looked
            // dead — the reported "Refresh does nothing". Always say something.
            setGscSyncNote({
                tone: "warn",
                text:
                    result.connector.detail ||
                    "The refresh ran, but Search Console sent nothing back. Check that the linked property covers this site.",
            });
        }
        // Success or failed-pull alike: the catalog now knows more than we do.
        onRefreshData?.();
    };

    /**
     * The crawl's internal link graph (ourteam-backend#4099).
     *
     * Read here for ONE reason: every link proposal is derived from this graph,
     * so when a page comes back with one suggested link against a recommended
     * six, the graph is what says whether the engine had candidates and passed
     * on them, or never had any to pass on. That question was previously only
     * answerable from a backend log, which is why it stayed open.
     *
     * `null` while loading and on failure alike — `linkGraphHealth` treats a
     * missing graph as "absent", which is a different report from "the crawl
     * found no links", and neither is presented as a page-level result.
     */
    const [linkGraph, setLinkGraph] = useState<AeoLinkGraph | null>(null);
    // In flight and answered-with-nothing are both `null`, and only the second
    // is a finding. Without this flag the note would assert "no link graph for
    // this site" for as long as the request takes, then take it back.
    const [linkGraphLoaded, setLinkGraphLoaded] = useState(false);
    useEffect(() => {
        setLinkGraph(null);
        setLinkGraphLoaded(false);
        if (!businessId) return;
        let cancelled = false;
        void getLinkGraph(businessId).then((g) => {
            if (cancelled) return;
            setLinkGraph(g);
            setLinkGraphLoaded(true);
        });
        return () => { cancelled = true; };
    }, [businessId]);
    const graph = linkGraphHealth(linkGraph);

    /**
     * The live workshop for whichever page is open (epic ourteam-backend#4104).
     *
     * `undefined` while there is no live business, which is what tells
     * PageWorkshopView to generate its sample page instead. On a live business
     * it is null until the fetch resolves, and stays null on failure — the
     * fixture must never stand in for a real client's page.
     */
    /**
     * Every page opened this session, by url.
     *
     * Kept because re-reading a page the operator has already looked at is not
     * free and not fast: it is a live fetch of the client's site plus a draft
     * read, so without this, stepping back to the list and returning replays
     * the whole "Reading the page…" state on something already in hand. Worse,
     * a generated draft would appear to have been thrown away — the operator
     * has no way to tell a slow re-read from a lost result, and the obvious
     * response is to press Draft proposals again, which spends another model
     * call for a draft that already exists.
     */
    const [workshops, setWorkshops] = useState<Record<string, PageWorkshop>>({});
    /**
     * Latest write intent per url, so an older in-flight read cannot undo a
     * newer one.
     *
     * A read is not the only writer of a page's draft — `generate()` writes one
     * too, and it can finish first. Without a token the two are ordered by
     * whoever happens to resolve last, which is arbitrary. Ordering by INTENT
     * instead: every read takes a number, every successful redraft burns one,
     * and a read whose number is no longer current does not get to speak about
     * the draft.
     *
     * A ref rather than state: this is bookkeeping about requests, not
     * something the UI renders, and it must be readable synchronously inside
     * the state updater.
     */
    const draftSeqRef = useRef<Record<string, number>>({});
    const [fetchingUrl, setFetchingUrl] = useState<string | null>(null);
    const [workshopError, setWorkshopError] = useState<string | null>(null);
    const [generating, setGenerating] = useState(false);
    /**
     * When we last read the client's page, per url, and whether the last
     * attempt failed (#3548).
     *
     * Client-side stamps, not BE fields: this answers "how old is the document
     * on my screen", which only the reader can know. `documentStale` is set
     * ONLY by a failed re-read — it means "we tried and could not, so what you
     * see is older than this", which is the one thing a reviewer publishing to
     * a live site must not be left to guess.
     */
    const [documentReadAt, setDocumentReadAt] = useState<Record<string, string>>({});
    const [documentStale, setDocumentStale] = useState<Record<string, boolean>>({});
    const [refreshingDocument, setRefreshingDocument] = useState<string | null>(null);


    /**
     * Which page's workshop is open, carried in the URL.
     *
     * A row's "View page details" action has to land on THAT page's workshop, and
     * the selection lives in the query string rather than in component state
     * so a deep link survives a refresh, a shared link and the back button.
     * Opening the Pages tab on its own has no param and therefore always
     * lands on the list — a remembered selection would reopen whatever was
     * clicked last, which is not what the tab means.
     *
     * On a live business the parameter is IGNORED outright rather than
     * answered with an explanation screen: a pasted or stale `?page=` should
     * leave no workshop-shaped surface at all, only the real page list.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const openUrl = workshopEnabled ? searchParams.get("page") : null;
    const workshopPage = openUrl
        ? { url: openUrl, title: searchParams.get("title") ?? openUrl }
        : null;
    const setWorkshopPage = (next: { url: string; title: string } | null) => {
        const params = new URLSearchParams(searchParams);
        if (next) {
            params.set("page", next.url);
            params.set("title", next.title);
        } else {
            params.delete("page");
            params.delete("title");
        }
        setSearchParams(params, { replace: !next });
    };

    const cachedPage = workshopPage ? workshops[workshopPage.url] : undefined;
    /** `undefined` = fixture business (the view generates its sample page). */
    const livePage = businessId ? (cachedPage ?? null) : undefined;
    /**
     * The sample business on the dev server only (H20a, scope 2.23): its
     * own Nyonya Table page, worded from the client's answer in the Answer
     * Plan, with a draft id and a publish the server refuses for the
     * client's yes, so the refusal can be seen. Undefined everywhere else.
     */
    const samplePublish = useMemo(() => (businessId ? undefined : samplePagePublish(business.id)), [businessId, business.id]);
    const sampleUrl = workshopPage?.url ?? null;
    const sampleTitle = workshopPage?.title ?? "";
    // Ask Oura (behind VITE_ENABLE_OURA_ASK, sample business only): the
    // answers the client approved in the Answer Plan sit on the page they
    // belong to as proposals, marked as approved, ahead of Oura's own. Off,
    // nothing is read and the sample page is exactly as before.
    const planAnswers = usePlanAnswers(business.id, ASK_OURA_ON && !!samplePublish);
    const samplePage = useMemo(() => {
        if (!samplePublish || !sampleUrl) return undefined;
        const base = nyonyaSamplePage(sampleUrl, sampleTitle);
        // samplePublish exists only for the sample business on the dev server, so this is the sample's page.
        const plan = planProposals(planAnswers, sampleUrl, true);
        if (!plan) return base;
        const at = Math.min(2, base.blocks.length);
        return {
            ...base,
            blocks: [...base.blocks.slice(0, at), ...plan.blocks, ...base.blocks.slice(at)],
            recommendations: [...plan.recs, ...base.recommendations],
        };
    }, [samplePublish, sampleUrl, sampleTitle, planAnswers]);
    const flashPlan = ASK_OURA_ON && searchParams.get("from") === "plan";
    // Ask Oura (PREVIEW): the approved answers' proposals on this page, and a
    // named person's yes in front of the workshop's own copy or publish when
    // every change in the set is one of them. Nothing here writes.
    const planIds = useMemo(
        () => new Set((samplePage?.recommendations ?? []).filter((r) => r.kind === "copy" && r.origin).map((r) => r.id)),
        [samplePage],
    );
    const signedIn = useSignedInName();
    /** The page the yes was asked on travels with it, so it can only be answered there. */
    const [yesAsk, setYesAsk] = useState<{
        pageUrl: string;
        changes: YesChange[];
        resolve: (yes: boolean) => void;
    } | null>(null);
    /** A yes waiting on the workshop's copy result before the chat reports it, scoped to the page it was given on. */
    const pendingYes = useRef<{ pageUrl: string; actor: string; count: number } | null>(null);
    // Leaving the page a yes was asked on answers it "no". Its publish flow is
    // still awaiting that answer inside the view, and must not continue there
    // once another page is on screen.
    useEffect(() => {
        if (!yesAsk || yesAsk.pageUrl === workshopPage?.url) return;
        yesAsk.resolve(false);
        setYesAsk(null);
    }, [workshopPage?.url, yesAsk]);
    // A yes already given (its copy still pending) is dropped once another page is on screen, so a later copy elsewhere never reports it.
    useEffect(() => {
        if (pendingYes.current && pendingYes.current.pageUrl !== workshopPage?.url) pendingYes.current = null;
    }, [workshopPage?.url]);
    const onlyPlan = (ids: readonly string[]) => ids.length > 0 && ids.every((id) => planIds.has(id));
    /** Only a page we have NOTHING for shows the reading state. A cached page
     *  renders at once and revalidates behind it. */
    const workshopLoading =
        (!!fetchingUrl && fetchingUrl === workshopPage?.url && !cachedPage) || generating;

    /** This page's Search Console row, when GSC supplied one. Real numbers or
     *  nothing — never zeros standing in for "not measured". */
    const metricsFor = (url: string): PageSearchMetrics | null => {
        const p = liveSearch?.pages?.find((r) => r.url === url);
        if (!p) return null;
        return { clicks: p.clicks, impressions: p.impressions, ctr: p.ctr, position: p.position };
    };

    /**
     * Load the open page's workshop: the extracted document, whatever draft
     * already exists for it, and its history.
     *
     * Content is fetched but generation is NOT triggered — opening a page must
     * not silently spend a model call on a client's site. `getPageDraft`
     * returns what has already been drafted; asking for new proposals is the
     * operator's explicit action (see `generate` below).
     */
    useEffect(() => {
        if (!businessId || !workshopPage) {
            setWorkshopError(null);
            return;
        }
        let cancelled = false;
        const url = workshopPage.url;
        const hadCached = !!workshops[url];
        // Claim this read's place in the order BEFORE awaiting anything.
        const seq = (draftSeqRef.current[url] ?? 0) + 1;
        draftSeqRef.current[url] = seq;
        setFetchingUrl(url);
        setWorkshopError(null);
        (async () => {
            const [content, draft, history] = await Promise.all([
                getPageContent(businessId, url),
                getPageDraft(businessId, url),
                getPageHistory(businessId, url),
            ]);
            if (cancelled) return;
            setFetchingUrl((cur) => (cur === url ? null : cur));
            if (!content) {
                // A revalidation that fails leaves what we already have on
                // screen: the page the operator is reading is still the page
                // they were reading, and blanking it to report a background
                // failure would be a worse lie than the slightly stale copy.
                if (hadCached) {
                    // Kept on screen, but no longer presented as current: the
                    // reviewer is deciding what to write to a live site from
                    // this text (#3548).
                    setDocumentStale((prev) => ({ ...prev, [url]: true }));
                    return;
                }
                // The BE distinguishes fetch_failed / not_html / no_text, but
                // only in its own error body; until that is surfaced this says
                // what is actionable without pretending to know which it was.
                setWorkshopError(
                    "Oura could not read this page. It may be unreachable, or built so that its text only appears after the page runs its scripts.",
                );
                return;
            }
            // A successful read is the document's new age, and clears any
            // staleness note a previous failure left behind.
            setDocumentReadAt((prev) => ({ ...prev, [url]: new Date().toISOString() }));
            setDocumentStale((prev) => (prev[url] ? { ...prev, [url]: false } : prev));
            const next = adaptPageWorkshop({
                content,
                draft,
                history,
                metrics: metricsFor(url),
                // Not plumbed yet: the voice profile lives on the scan results,
                // which this screen does not receive. The workshop renders "not
                // derived" rather than inventing one.
                voice: null,
            });
            // Take the fresh read — the BE re-anchors every proposal against
            // the LIVE page on each read, so `match`/`matchedText` can
            // legitimately differ from what we cached a minute ago, and keeping
            // stale anchors is how a reviewer approves a highlight that no
            // longer exists on the page.
            //
            // EXCEPT when the fresh read carries no proposals and we already
            // hold some. "The response contained no draft" is not the same
            // claim as "this page has no draft", and treating them as the same
            // is destructive: it silently discards work the operator watched
            // Oura produce, and the only recovery on offer is the Draft
            // proposals button — which spends another model call and returns a
            // DIFFERENT set, because generation is not deterministic.
            //
            // Observed on Kheng Dental: History listed "Drafted from the scan,
            // 20 proposals" and "Redrafted, 15 proposals" — both real, both
            // stored server-side — while the draft read returned nothing and
            // the pane offered to draft again. Keeping what we have makes the
            // rule the operator expects: a draft survives until they redraft it.
            //
            // The check runs INSIDE the updater, against `prev`, because the
            // render-time `workshops` is stale by the time three awaits have
            // resolved. Concretely: open a page with nothing cached, and while
            // this read is in flight press Draft proposals. `generate()` writes
            // 20 proposals into the cache; this read then returns none; and a
            // check against the closure — where the entry was still undefined —
            // computes `keepExisting: false` and overwrites them. The draft the
            // operator just watched Oura produce, destroyed by a request that
            // started before it existed.
            setWorkshops((prev) => {
                const existing = prev[url]?.recommendations ?? [];
                // Superseded: a redraft landed while this read was in flight,
                // so whatever draft it is carrying is older than what we hold —
                // including a NON-empty one. Emptiness was never the thing that
                // made a response unsafe; being out of date was.
                const superseded = draftSeqRef.current[url] !== seq;
                const keepExisting =
                    superseded || (next.recommendations.length === 0 && existing.length > 0);
                return {
                    ...prev,
                    // The page CONTENT is still taken from this read even when
                    // it is superseded. Only the draft is contested — nothing
                    // else writes blocks, history or metrics, and dropping the
                    // whole response would throw away the only copy of the
                    // extracted document we have. `generate()` writes an entry
                    // with `blocks: []` when it wins the race, so discarding
                    // this response wholesale would leave a real draft rendered
                    // against a blank page.
                    [url]: keepExisting ? { ...next, recommendations: existing } : next,
                };
            });
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, workshopPage?.url]);

    /** Ask Oura for proposals on the open page. A real model call on a real
     *  client page, so it is only ever reached from a button.
     *
     *  With `notes` this is a REDRAFT: the reviewer's per-proposal notes go up
     *  with the request (BE resolves them against the previous draft's
     *  proposals and injects them into the prompt, outranking the model's
     *  earlier choices). Caps are the endpoint's: 25 notes, 500 chars each. */
    const generate = async (notes?: { recommendation_id: string; note: string }[]) => {
        if (!businessId || !workshopPage || generating) return;
        const url = workshopPage.url;
        const trimmedNotes = (notes ?? [])
            .map((n) => ({ recommendation_id: n.recommendation_id, note: n.note.trim().slice(0, 500) }))
            .filter((n) => n.note.length > 0)
            .slice(0, 25);
        setGenerating(true);
        const draft = await createPageDraft(businessId, {
            url,
            ...(trimmedNotes.length > 0 ? { notes: trimmedNotes } : {}),
        });
        setGenerating(false);
        if (!draft) {
            setWorkshopError(
                trimmedNotes.length > 0
                    ? "Oura could not redraft this page. Your notes are kept — try again."
                    : "Oura could not draft proposals for this page. Nothing was changed.",
            );
            return;
        }
        const history = await getPageHistory(businessId, url);
        // Burn the token: this draft is now the newest intent for this page, so
        // any read still in flight — including one that will return a perfectly
        // valid, merely OLDER draft — is no longer allowed to write over it.
        draftSeqRef.current[url] = (draftSeqRef.current[url] ?? 0) + 1;
        // Reuse the extracted document we already hold rather than re-reading
        // the client's site: drafting did not change their page, so a second
        // fetch would cost them a request and us a wait for the same bytes.
        const existing = workshops[url];
        setWorkshops((prev) => ({
            ...prev,
            [url]: {
                ...(existing ?? {}),
                ...adaptPageWorkshop({
                    content: {
                        url,
                        title: existing?.title ?? workshopPage.title,
                        blocks: [],
                    },
                    draft,
                    history,
                    metrics: metricsFor(url),
                    voice: null,
                }),
                // The adapter above rebuilds everything from the draft; the
                // document itself comes from what we already read.
                blocks: existing?.blocks ?? [],
                role: existing?.role ?? "content page",
                linkBudget: existing?.linkBudget ?? 3,
            },
        }));
        // A redraft supersedes the whole draft: the notes now live inside it
        // (History records them as the instruction) and the proposal ids the
        // staged set pointed at no longer exist — a kept review would show
        // stale notes against proposals that already answered them.
        if (trimmedNotes.length > 0) {
            setReviews((prev) => ({ ...prev, [url]: EMPTY_REVIEW }));
        }
    };

    /**
     * Re-read the client's page and replace the extracted document with what it
     * says NOW (#3548).
     *
     * Only the DOCUMENT is replaced — draft, history and metrics are left
     * alone, because this answers "what does the page say", not "what has Oura
     * proposed". Callers that need both (publish) refresh them separately.
     *
     * A failure does not blank the document. The reviewer keeps the text they
     * were reading, flagged as older than this attempt: a blank pane loses the
     * only copy of the page we have, and silently keeping stale text while
     * presenting it as current is how a reviewer approves a change against
     * copy that no longer exists.
     */
    const refreshDocument = async (url: string): Promise<boolean> => {
        if (!businessId) return false;
        setRefreshingDocument(url);
        const content = await getPageContent(businessId, url);
        setRefreshingDocument((cur) => (cur === url ? null : cur));
        if (!content) {
            setDocumentStale((prev) => ({ ...prev, [url]: true }));
            return false;
        }
        const fresh = adaptPageWorkshop({
            content,
            draft: null,
            history: [],
            metrics: null,
            voice: null,
        });
        setDocumentReadAt((prev) => ({ ...prev, [url]: new Date().toISOString() }));
        setDocumentStale((prev) => ({ ...prev, [url]: false }));
        setWorkshops((prev) => {
            const existing = prev[url];
            if (!existing) return prev;
            // Document fields only. Running the adapter with empty draft and
            // history is what gives us block adaptation for free; taking its
            // `recommendations`/`history` would wipe both with those empties.
            return {
                ...prev,
                [url]: {
                    ...existing,
                    title: fresh.title,
                    blocks: fresh.blocks,
                    role: fresh.role,
                    linkBudget: fresh.linkBudget,
                },
            };
        });
        return true;
    };

    /**
     * Write a page's kept proposals to the live site (ourteam-backend#4100).
     *
     * The one call in this screen that changes a client's public page. The view
     * owns the confirmation and the outcome banner; this owns the request and
     * the refresh — on success it re-reads the draft and history so the History
     * tab shows the publication and the draft reflects its published status.
     *
     * The result is handed straight back for the view to render. A failure is
     * NOT a refresh: the page is unchanged, so what is on screen is still true.
     */
    const publish = async (args: { draftId: string; dismissed: string[] }): Promise<AeoPagePublishResult> => {
        if (!businessId || !workshopPage) {
            return { ok: false, kind: "error", message: "No page is open to publish." };
        }
        const url = workshopPage.url;
        const res = await publishPageChanges(businessId, {
            url,
            draftId: args.draftId,
            dismissed: args.dismissed,
        });
        if (!res.ok) return res;

        // Burn the token so any read still in flight cannot overwrite the
        // published state with a pre-publish draft — the same guard generate
        // uses.
        draftSeqRef.current[url] = (draftSeqRef.current[url] ?? 0) + 1;
        // The document IS re-read here, unlike after a redraft (#3548).
        // Drafting does not touch the client's page, so reusing the bytes we
        // hold is free and correct. Publishing DOES touch it — keeping the
        // pre-publish text would leave the reviewer reading a document that no
        // longer matches the site they just wrote to, with the paragraph they
        // changed still showing its old wording.
        setRefreshingDocument(url);
        const [content, draft, history] = await Promise.all([
            getPageContent(businessId, url),
            getPageDraft(businessId, url),
            getPageHistory(businessId, url),
        ]);
        setRefreshingDocument((cur) => (cur === url ? null : cur));
        if (content) {
            setDocumentReadAt((prev) => ({ ...prev, [url]: new Date().toISOString() }));
            setDocumentStale((prev) => ({ ...prev, [url]: false }));
        } else {
            // The publish itself succeeded — that is what `res` reports. Only
            // the re-read failed, so the old document stays up, marked stale.
            setDocumentStale((prev) => ({ ...prev, [url]: true }));
        }
        const existing = workshops[url];
        setWorkshops((prev) => ({
            ...prev,
            [url]: {
                ...(existing ?? {}),
                ...adaptPageWorkshop({
                    content: content ?? {
                        url,
                        title: existing?.title ?? workshopPage.title,
                        blocks: [],
                    },
                    draft,
                    history,
                    metrics: metricsFor(url),
                    voice: null,
                }),
                // Only when the re-read failed does the old document stand in.
                ...(content
                    ? {}
                    : {
                        blocks: existing?.blocks ?? [],
                        role: existing?.role ?? "content page",
                        linkBudget: existing?.linkBudget ?? 3,
                    }),
            },
        }));
        return res;
    };

    /**
     * The reviewer's corrections to the inferred page role, by url.
     *
     * Role is inferred from the URL, which works on a well-structured site and
     * falls back to "Content Page" on the flat-slug sites most clients run — so
     * a correction has to be possible, and it has to be the value the link
     * engine then uses.
     *
     * Seeded from localStorage so the table paints the corrections this browser
     * already knows about without waiting for the network, then reconciled with
     * the server's set, which is the SHARED answer.
     */
    const [roleOverrides, setRoleOverrides] = useState<Record<string, PageRole>>(() =>
        loadStoredRoles(businessId),
    );
    useEffect(() => {
        storeRoles(businessId, roleOverrides);
    }, [businessId, roleOverrides]);

    /**
     * Pull everybody else's corrections in (ourteam-backend#4368 §2).
     *
     * The whole point of the set-aside roles is that one person sorts the
     * placeholder pages out ONCE and the team sees it. Until the backend enum
     * held all nine roles these labels were suppressed before the request and
     * lived in a single browser, so a second reviewer opened the same crawl and
     * saw none of the first one's work.
     *
     * Server wins on conflict. It is the shared record, and a stale local entry
     * is exactly the thing this call exists to correct — a page somebody
     * re-labelled from another machine must not keep this browser's older
     * answer. Local-only entries survive: an unsynced correction (or one made
     * while offline) is still the reviewer's, not something to silently drop.
     */
    useEffect(() => {
        if (!businessId) return;
        let cancelled = false;
        void fetchStoredRoles(businessId, business.domain).then((server) => {
            if (cancelled || Object.keys(server).length === 0) return;
            setRoleOverrides((local) => ({ ...local, ...server }));
        });
        return () => { cancelled = true; };
    }, [businessId, business.domain]);
    /** Apply a correction: locally first (the label the table reads, instantly),
     *  then mirrored to the BE, which is where the rest of the team reads it. */
    const setRole = (url: string, role: PageRole) => {
        setRoleOverrides((prev) => ({ ...prev, [url]: role }));
        persistRole(businessId, url, role);
    };

    /**
     * Placeholder pages taken off the live site in this session (#4529 §3).
     *
     * Recorded locally because the crawl is a snapshot: the page is now a draft
     * in WordPress, and the row it came from stays in this table until the next
     * scan. Without this the action would appear to do nothing, and the obvious
     * response to that is to click it again.
     */
    const [removing, setRemoving] = useState<{ url: string; title: string } | null>(null);
    const [removeBusy, setRemoveBusy] = useState(false);
    const [removeError, setRemoveError] = useState<string | null>(null);
    const [removed, setRemoved] = useState<Set<string>>(new Set());

    const confirmRemove = async () => {
        if (!businessId || !removing) return;
        setRemoveBusy(true);
        setRemoveError(null);
        const res = await setPageStatus(businessId, removing.url, "draft");
        setRemoveBusy(false);
        if (!res.ok) {
            // The backend's own sentence, not a translation of it. A 409 names
            // WHICH guard refused — "a reviewer labelled it product", "the last
            // crawl did not reach it" — and those need different next steps.
            setRemoveError(res.message);
            return;
        }
        setRemoved((cur) => new Set(cur).add(removing.url));
        setRemoving(null);
    };
    /** Every page's review so far, kept HERE so following a two-way link to
     *  another page and coming back does not discard the work — and mirrored
     *  to localStorage so a reload does not discard it either. */
    const [reviews, setReviews] = useState<Record<string, PageReview>>(() =>
        loadStoredReviews(businessId),
    );
    useEffect(() => {
        if (!businessId) return;
        try {
            localStorage.setItem(reviewStorageKey(businessId), JSON.stringify(reviews));
        } catch {
            // Storage full or blocked — the in-memory review still works; it
            // just will not survive the next reload.
        }
    }, [businessId, reviews]);
    /**
     * Every page's kind, by normalised URL, from whichever payload reported it.
     *
     * Hoisted out of the rows memo below because `roleOf` needs it too: it is
     * what defaults a generated index to `utility` now that indexes are listed
     * rather than dropped.
     */
    const kindByUrl = useMemo(() => {
        const map = new Map<string, PageKind>();
        const remember = (url: string, kind: PageKind | undefined) => {
            const fixed = correctedKind(url, kind);
            if (fixed) map.set(matchKey(url), fixed);
        };
        audit.priorityPages.forEach((p) => remember(p.url, p.pageKind));
        audit.underperforming.forEach((p) => remember(p.url, p.pageKind));
        (audit.crawledPages ?? []).forEach((p) => {
            if (typeof p?.url === "string" && p.url.trim() !== "") remember(p.url, p.pageKind);
        });
        return map;
    }, [audit.priorityPages, audit.underperforming, audit.crawledPages]);

    /**
     * A page's role: the reviewer's label first, then the crawl's own
     * classification, then inference from the URL.
     *
     * The middle step is what keeps a generated index out of copy work now that
     * the table lists it instead of hiding it. `utility` is exactly the role's
     * meaning: a page that does something rather than answering something.
     * A reviewer can still override it, which they could not do while the row
     * did not exist.
     */
    const roleOf = (url: string): PageRole =>
        roleOverrides[url]
        ?? (isGeneratedIndex(kindByUrl.get(matchKey(url))) ? "utility" : roleForUrl(url));

    const rows = useMemo<PageRow[]>(() => {
        const findingByUrl = new Map(audit.underperforming.map((p) => [p.url, p.reason]));

        // Every site-audit finding, pivoted from "which pages have this issue"
        // to "which issues does this page have".
        //
        // Built by INVERTING `audit.issues[].pages[]` rather than by reading
        // `crawledPages[].failed[]`, even though the second is the field that
        // exists for this. Two reasons: the per-issue lists are present on every
        // run including ones finalized before the inventory shipped, and on the
        // sample businesses — so one code path covers everything — and they are
        // the same lists the Site Audit tab renders, which is what guarantees
        // the two tabs agree page for page rather than merely usually.
        //
        // The inventory is still read below, for the one thing only it can say:
        // that a page was fetched and passed every check.
        //
        // Keyed on `matchKey` because these lists and the search/crawl lists
        // spell the same page differently (scheme, `www.`, trailing slash) — the
        // same normalisation `push` de-dupes on further down.
        //
        // `pages` and every `url` are guarded rather than trusted. Both arrive
        // from the wire, `matchKey` calls `.trim()` on whatever it is handed,
        // and an uncaught TypeError here is a white screen for the whole tab —
        // which is precisely how this file lost the Pages tab once already when
        // the link graph turned out to carry entries with no path (see
        // `graphPages` below). A type that promises `string` is not a string
        // arriving.
        const issuesByUrl = new Map<string, Array<{ key: string; label: string }>>();
        // The absolute URL behind each normalised key, so a page that ONLY the
        // findings know about can still be given a row further down.
        const urlForIssueKey = new Map<string, string>();
        audit.issues.forEach((issue) => {
            (issue.pages ?? []).forEach((p) => {
                if (typeof p?.url !== "string" || p.url.trim() === "") return;
                const key = matchKey(p.url);
                if (!urlForIssueKey.has(key)) urlForIssueKey.set(key, p.url);
                const list = issuesByUrl.get(key);
                if (list) list.push({ key: issue.key, label: issue.label });
                else issuesByUrl.set(key, [{ key: issue.key, label: issue.label }]);
            });
        });
        /**
         * Pages the crawl read and found no <title> on.
         *
         * Narrow on purpose. A 4xx is excluded because a page that does not
         * serve has no title by definition and the row already says "404";
         * anything that is not HTML is excluded because a PDF was never going
         * to have one. What survives is a real, fixable SEO defect on a real
         * page — which is why it can be stated as a finding instead of leaving
         * a reader to wonder why some rows show a name and others an address.
         */
        const noTitleKeys = new Set(
            (audit.crawledPages ?? [])
                .filter(
                    (p) =>
                        typeof p?.url === "string" &&
                        p.url.trim() !== "" &&
                        (p.title ?? "").trim() === "" &&
                        (p.status === undefined || (p.status >= 200 && p.status < 300)) &&
                        (!p.contentType || p.contentType.includes("html")),
                )
                .map((p) => matchKey(p.url)),
        );

        // Pages the crawler read. A page in here with no entry above was
        // measured and passed — a result, and different from a page we simply
        // never fetched, which is why this is a set rather than a default of [].
        const crawledKeys = audit.crawledPages
            ? new Set(
                  audit.crawledPages.flatMap((p) =>
                      typeof p?.url === "string" && p.url.trim() !== "" ? [matchKey(p.url)] : [],
                  ),
              )
            : null;
        /**
         * The page's own <title>, from whichever source knows it.
         *
         * Seeded from the crawl inventory as well as the two curated lists, and
         * keyed on the NORMALISED url. Both of those were the bug: the map held
         * only `priorityPages` + `underperforming` — twelve rows each at most —
         * and a Search Console row looked itself up in it and found nothing. The
         * crawl inventory has the title for every page it read, but the pass
         * that reads it runs later and `push` de-dupes, so a page already listed
         * by Search Console never reached it.
         *
         * The effect was backwards from what anyone would guess: the pages MOST
         * likely to show a bare path were the ones with the most traffic, since
         * those are exactly the pages Search Console reports. On Blanc Dental
         * that meant the homepage, at 63 clicks, rendered as "/" while a
         * zero-click page two rows down had its full title. Nothing was missing
         * from the payload; the two halves were keyed differently and joined in
         * the wrong order.
         */
        const titleByKey = new Map<string, string>();
        const rememberTitle = (url: unknown, title: unknown) => {
            if (typeof url !== "string" || url.trim() === "") return;
            // An empty title is the crawler saying it found no <title>. It must
            // not be stored as one, or it would mask a real title that another
            // source has for the same page.
            if (typeof title !== "string" || title.trim() === "") return;
            const key = matchKey(url);
            if (!titleByKey.has(key)) titleByKey.set(key, title);
        };
        audit.priorityPages.forEach((p) => rememberTitle(p.url, p.title));
        audit.underperforming.forEach((p) => rememberTitle(p.url, p.title));
        (audit.crawledPages ?? []).forEach((p) => rememberTitle(p.url, p.title));
        const titleFor = (url: string) => titleByKey.get(matchKey(url));

        // What the crawl decided each URL is. Built from both lists so a page
        // reaching this table through Search Console still gets classified — GSC
        // reports impressions for archive pages too, and its rows carry no kind
        // of their own.
        // Keyed on `matchKey`, like every other join in this function. It used to
        // be keyed on the raw url and read with the raw url, which happened to
        // work only while both sides came from the same payload — the moment the
        // inventory below started contributing rows it would have silently
        // stopped matching, and an index page would have slipped back in.
        // Link-graph facts keyed by URL, joined onto EVERY row rather than only
        // the inventory ones — a page on the assessment's shortlist benefits
        // from its inbound count just as much, and this tab is where internal
        // linking gets fixed.
        //
        // The graph reports a site-relative `path`; everything else in this
        // table carries an absolute URL. They only meet after resolving against
        // the business domain and normalising through `matchKey`. Keying on the
        // raw value is why "Links in" read "—" on every row: the map held
        // "/our-story" and was being asked for
        // "https://beechoo.com.my/our-story".
        //
        // Entries without a usable path are dropped rather than carried as
        // half-rows. A row with no address cannot be identified, linked or
        // opened, and it can take the screen down: `roleForUrl` catches the
        // throw from `url.startsWith`, then runs `path.toLowerCase()` on the
        // undefined it fell back to, OUTSIDE that try.
        const graphPages = (linkGraph?.pages ?? []).flatMap((p) => {
            if (typeof p?.path !== "string" || p.path.trim() === "") return [];
            try {
                return [{ ...p, url: new URL(p.path, `https://${business.domain}`).toString() }];
            } catch {
                return [];
            }
        });
        const linkByUrl = new Map(graphPages.map((p) => [matchKey(p.url), p]));

        const joined: PageRow[] = [];
        const seen = new Set<string>();
        const push = (row: PageRow) => {
            // De-duped on the normalised key, not the raw string, so the same
            // page arriving from two sources in two spellings is one row.
            const key = matchKey(row.url);
            // Only assets and duplicates are dropped now. Every page the crawl
            // reached gets a row, because the Site Audit tab reports findings
            // against ALL of them — a page in one list and not the other is the
            // mismatch this table kept being reported for.
            if (isAssetUrl(row.url) || seen.has(key)) return;
            seen.add(key);
            const g = linkByUrl.get(key);
            // The page's own findings, joined on the same normalised key.
            //
            // `[]` only when the crawler actually read this page and it failed
            // nothing. Otherwise undefined — a page Search Console reported but
            // the crawl never fetched has an UNKNOWN problem list, not an empty
            // one, and the cell must not tell the reviewer it is clean.
            const issues =
                issuesByUrl.get(key) ?? (crawledKeys?.has(key) ? [] : undefined);
            const missingTitle = noTitleKeys.has(key);
            joined.push(
                g
                    ? {
                          ...row,
                          issues,
                          missingTitle,
                          inboundInBody: g.inboundInBody ?? g.inbound,
                          orphan: g.orphan,
                      }
                    : { ...row, issues, missingTitle },
            );
        };

        if (gscConnected && liveSearch?.pages) {
            // GSC drives the list; crawl findings annotate it.
            liveSearch.pages.forEach((p) =>
                push({
                    url: p.url,
                    title: titleFor(p.url),
                    clicks: p.clicks,
                    impressions: p.impressions,
                    ctr: p.ctr,
                    position: p.position,
                    flag: classify(p.impressions, p.ctr, p.position),
                    finding: findingByUrl.get(p.url) ?? null,
                    chosen: false,
                }),
            );
        } else {
            joined.length = 0;
            audit.priorityPages.forEach((p) =>
                push({
                    url: p.url,
                    title: p.title,
                    clicks: null,
                    impressions: null,
                    ctr: null,
                    position: null,
                    flag: null,
                    finding: findingByUrl.get(p.url) ?? null,
                    chosen: false,
                }),
            );
        }
        // Crawl-flagged pages GSC never showed (orphans have no impressions) —
        // appended so an orphan cannot hide by having no search history.
        audit.underperforming.forEach((p) =>
            push({
                url: p.url,
                title: p.title,
                clicks: null,
                impressions: null,
                ctr: null,
                position: null,
                flag: null,
                finding: p.reason,
                chosen: false,
            }),
        );

        // The crawl inventory: every page the crawl reached that the assessment
        // did not flag.
        //
        // Without this the table rendered the shortlist alone — four rows under
        // a header announcing a thirty-page crawl — and there was no way to see
        // what was read and passed. `push` de-dupes, so a page already listed
        // above keeps its flagged row, its finding and its rank.
        //
        // `results.audit.crawledPages` (ourteam-backend#3957) is the source now
        // that it is on the wire — the swap the previous comment here asked for.
        //
        // It matters because the link graph is NOT the crawl: it enumerates
        // pages by their internal links, so a page nothing links to never
        // appears in it. Those pages were still being listed by the Site Audit
        // tab, which reads the checks directly — so an orphaned post showed up
        // under Thin content and then could not be found in this table at all,
        // which is the "Issues and Crawled Pages do not tally" report.
        //
        // The graph remains the fallback for runs finalized before the
        // inventory shipped, and for the sample businesses, which have no
        // `crawledPages` at all. `push` de-dupes, so anything already listed
        // above keeps its flagged row, its finding and its rank.
        const inventory: { url: string; title?: string }[] =
            audit.crawledPages && audit.crawledPages.length > 0
                ? audit.crawledPages.flatMap((p) =>
                      typeof p?.url === "string" && p.url.trim() !== ""
                          ? [{ url: p.url, title: p.title }]
                          : [],
                  )
                : graphPages.map((p) => ({ url: p.url, title: p.title }));
        inventory.forEach((p) =>
            push({
                url: p.url,
                // `||`, not `??`: both sources send "" for a page with no
                // <title>, and `??` treats an empty string as a value — so the
                // fallback never ran and the row rendered a blank name.
                title: p.title || titleFor(p.url),
                clicks: null,
                impressions: null,
                ctr: null,
                position: null,
                flag: null,
                finding: null,
                chosen: false,
                inventoryOnly: true,
            }),
        );

        /**
         * Anything the Site Audit flags that none of the lists above produced.
         *
         * This is the guarantee, not an optimisation: every page under a
         * finding HAS a row here, so "it is in Issues but not in Crawled Pages"
         * cannot happen again for any reason — a page missing from the crawl
         * inventory, a source we have not thought of, or a future check that
         * reports a URL from somewhere else entirely.
         *
         * Previous fixes for this report each addressed one cause (the link
         * graph as the inventory source, then the archive misclassification,
         * then generated indexes being dropped). This closes the class.
         *
         * `push` de-dupes, so a page already listed keeps its richer row.
         */
        issuesByUrl.forEach((_findings, key) => {
            if (seen.has(key)) return;
            const url = urlForIssueKey.get(key);
            if (!url) return;
            push({
                url,
                title: titleFor(url),
                clicks: null,
                impressions: null,
                ctr: null,
                position: null,
                flag: null,
                finding: null,
                chosen: false,
                inventoryOnly: true,
            });
        });

        // Site order: home, then top-level pages, then their children. The
        // table is the crawl inventory now, so it reads as a map of the site;
        // what needs work is carried by the Why-flagged column and by the
        // chosen badge, not by position. `chosen` is re-pinned to the top
        // below, so an explicit Oura pick still leads.
        const ranked = [...joined].sort((a, b) => {
            const ka = hierarchyKey(a.url);
            const kb = hierarchyKey(b.url);
            if (ka.depth !== kb.depth) return ka.depth - kb.depth;
            return ka.sort.localeCompare(kb.sort);
        });
        // ...but ONLY when Search Console is connected. Both discriminating
        // signals are GSC-derived — `flag` needs impressions, CTR and position,
        // and the tie-break is impressions — so without it every page with any
        // crawl finding ties at the same weight and `.slice(0, 5)` is really
        // "the first five rows". Badging that as CHOSEN BY OURA claims a
        // judgement that was never made, and the operator rightly asks what it
        // was based on when they have connected nothing. An unearned
        // recommendation costs more trust than no recommendation.
        //
        // chosenCount then falls to 0, which also drops the "Oura picked 5 to
        // start with" line from the card header. The Connect-Search-Console
        // nudge directly above the table is what fills the gap, and it now
        // describes something the operator can actually see the absence of.
        //
        // Set-aside pages are excluded from the picks BEFORE the slice, not
        // hidden after it. This is the failure the two roles were added for: on
        // a shop, Oura spent picks on product pages nobody was ever going to
        // rewrite, and on a client with a drawer of unpublished stubs it
        // nominated pages whose fix is deletion. Filtering them out of the list
        // and leaving the slice at five would have quietly shrunk the working
        // set to two; filtering first means the five picks are five real ones.
        if (gscConnected) {
            ranked
                .filter(
                    (r) =>
                        (r.flag || (r.finding && r.finding !== "404")) &&
                        !isSetAsideRole(roleOf(r.url)),
                )
                .slice(0, WORKING_SET_SIZE)
                .forEach((r) => { r.chosen = true; });
        }

        return ranked.sort((a, b) => Number(b.chosen) - Number(a.chosen));
        // `business.domain` resolves the graph's relative paths, so a business
        // switch must rebuild these rows or the new site's paths would be
        // resolved against the previous site's domain. `roleOverrides` is a
        // dependency because the picks read it: labelling a chosen page as a
        // product page has to take the badge off it there and then. (`roleOf`
        // is a plain closure over exactly that state, so listing it as well
        // would only rebuild the join on every render.)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audit, gscConnected, liveSearch, linkGraph, business.domain, roleOverrides]);

    const chosenCount = rows.filter((r) => r.chosen).length;
    /** The assessment's shortlist, as opposed to the rest of the crawl. */
    const flaggedCount = rows.filter((r) => !r.inventoryOnly).length;

    /**
     * The view controls. All three are presentation over the SAME `rows` — none
     * of them re-derives a picked page or a diagnosis, so what a reviewer sorts
     * or searches can never change what Oura claimed about a page.
     */
    const [view, setView] = useState<TableView>("work");
    const [query, setQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState<"all" | PageRole>("all");
    /**
     * `null` = "whatever the default is", resolved below rather than baked into
     * the initial state. The default depends on `gscConnected`, which arrives
     * AFTER the first render on a live business: a state initialiser would have
     * settled on site order and then never revisited it, so the table would
     * open on clicks-descending for a reload and site order for a fresh load of
     * the same page. Storing the reviewer's explicit choice only, and computing
     * the rest, makes both paths agree.
     */
    const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);

    /**
     * Highest clicks first, as soon as there are clicks to sort by.
     *
     * The table lists the whole crawl in site order, which reads as a map of
     * the site — right for "what did Oura read", wrong for "what should I work
     * on next", which is the question this tab exists to answer. Where a page
     * sits in the URL tree says nothing about whether it earns anything.
     *
     * Without Search Console every metric is null, so a click sort would order
     * the table by nothing and present it as a ranking. Site order stays the
     * default there — an honest arrangement instead of a meaningless one.
     */
    const effectiveSort: { key: SortKey; dir: SortDir } =
        sort ?? (gscConnected ? { key: "clicks", dir: "desc" } : { key: "site", dir: "asc" });

    /**
     * Click a column: natural direction, then reversed, then **back to Oura's
     * own ranking**.
     *
     * The third state is the one worth having (adopted from asepulloh0109's
     * #3402 work on staging): a reviewer who sorts by CTR to answer one
     * question needs a way back to the arrangement the picks live in, and
     * hunting for the Page header to find it is a puzzle. Cycling through it on
     * the column they are already clicking is not.
     *
     * The comparison is against `effectiveSort`, not the raw state, so the
     * default clicks-descending PARTICIPATES in the cycle. Comparing against
     * `sort` would make the first click on Clicks re-apply what is already on
     * screen — a control that does nothing the first time you press it.
     */
    const onSort = (key: SortKey) => {
        setPage(1);
        setSort(() => {
            if (key === "site") return { key: "site", dir: "asc" };
            const cur = effectiveSort;
            if (cur.key !== key) return { key, dir: SORT_DEFAULT_DIR[key] };
            return cur.dir === SORT_DEFAULT_DIR[key]
                ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
                : { key: "site", dir: "asc" };
        });
    };

    /** How many rows each tab holds, counted before the search and the role
     *  filter narrow them — a tab's count describes the tab, not the query. */
    const viewCounts = useMemo(() => {
        let aside = 0;
        for (const r of rows) if (isSetAsideRole(roleOf(r.url))) aside += 1;
        return { work: rows.length - aside, all: rows.length, aside };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, roleOverrides]);

    /** The rows the current tab is about, before search and role narrow them.
     *  Kept separate so the Role dropdown can count within the tab. */
    const inView = useMemo(
        () =>
            rows.filter((r) => {
                const aside = isSetAsideRole(roleOf(r.url));
                return view === "all" ? true : view === "aside" ? aside : !aside;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, view, roleOverrides],
    );

    /** Per-role counts for the dropdown, scoped to the tab (house style §6a). */
    const roleCounts = useMemo(() => {
        const counts = {} as Record<PageRole, number>;
        for (const r of inView) {
            const role = roleOf(r.url);
            counts[role] = (counts[role] ?? 0) + 1;
        }
        return counts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, roleOverrides]);

    /** The set-aside roles this tab actually contains — the filter menu's second
     *  group, and whether it is worth drawing at all. */
    const asideRolesInView = SET_ASIDE_ROLES.filter((r) => (roleCounts[r] ?? 0) > 0);

    const visibleRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = inView.filter((r) => {
            if (roleFilter !== "all" && roleOf(r.url) !== roleFilter) return false;
            // Title AND url: a reviewer looking for the fees page searches
            // "fees", and on a site with untitled pages the slug is all there is.
            if (q && !`${r.title ?? ""} ${r.url}`.toLowerCase().includes(q)) return false;
            return true;
        });

        // Site order is the base arrangement `rows` already carries, chosen
        // pages pinned above it. Re-sorting it here would only undo that.
        if (effectiveSort.key === "site") return filtered;

        // A metric sort does NOT pin the picks: the reviewer asked for the
        // table in one specific order, and floating five rows above it would
        // silently answer a different question. The badge still marks them.
        const { key, dir } = effectiveSort;
        return [...filtered].sort((a, b) =>
            compareMetric(metricOf(a, key), metricOf(b, key), dir),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, query, roleFilter, effectiveSort.key, effectiveSort.dir, roleOverrides]);

    /** Never undefined — `view` only ever holds a value from this table. The
     *  fallback keeps the render honest rather than optional-chaining a label. */
    const activeTab = VIEW_TABS.find((t) => t.value === view) ?? VIEW_TABS[0];

    const filtersApplied = query.trim() !== "" || roleFilter !== "all";
    const clearFilters = () => {
        setQuery("");
        setRoleFilter("all");
    };
    /** The row set is built from two fetches that settle independently, so it
     *  can shrink under the reader — and a tab, a search or a role filter can
     *  shrink it too. Never strand them on a page that no longer exists (house
     *  style §7 resets paging when the set changes). */
    useEffect(() => {
        if (page > Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE))) setPage(1);
    }, [visibleRows.length, page]);
    useEffect(() => {
        setPage(1);
    }, [view, query, roleFilter]);
    const offset = (page - 1) * PAGE_SIZE;

    /**
     * "30 of 87 pages" — how much of the site the SCAN read, not how many pages
     * the site has. "5 pages found" stated a fact about the site when the truth
     * was a fact about the crawl: the site publishes 87 addresses and the
     * crawler stops at a ceiling by design. `pagesKnown` is the one stable
     * denominator (never `truncation.unvisited`, which counts the live crawl
     * queue and moves between runs). Older runs and fixtures have no
     * `pagesKnown`, so the row count keeps carrying the old copy there. Also
     * visible on the client-facing report — precision, not an apology.
     */
    const known = audit.pagesKnown ?? null;
    /**
     * Everything the card says about the crawl, as clauses on ONE line.
     *
     * These were three sentences across two blocks — a header paragraph naming
     * the domain, and a grey band under it for the link graph. The domain is
     * already in the page's own H1 block directly above, and the rest were
     * counts dressed as prose. Counts read faster as counts: four short clauses
     * separated by `·` say the same thing in a third of the space, and a reader
     * can find the one they came for without parsing a sentence.
     *
     * Anything that is a FINDING rather than a count — a truncated crawl, a
     * missing link graph — is deliberately not here; it goes to the band below,
     * where it is the only thing to read.
     */
    const headerFacts = [
        known !== null && known > 0
            ? `${audit.pagesCrawled} of ${known} known pages read`
            : `${audit.pagesCrawled} pages read`,
        // Not "to work on": the tab of that name counts every page not set
        // aside, and the two are different numbers for different things.
        `${flaggedCount} on the scan's shortlist`,
        chosenCount > 0 ? `${chosenCount} chosen by Oura` : null,
        businessId && linkGraphLoaded ? linkGraphClause(graph) : null,
    ].filter(Boolean) as string[];

    /**
     * The caveats, in reading order: how complete the crawl is first, then what
     * the link engine had. Both are conditional, so the usual card carries no
     * band at all.
     */
    const crawlFindings = [
        crawlTruncationFinding(audit.truncated, audit.truncationReason, audit.pagesCrawled, known),
        businessId && linkGraphLoaded ? linkGraphFinding(graph, linkGraph) : null,
    ].filter(Boolean) as string[];

    /**
     * The site audit's findings for the page the workshop has open.
     *
     * Resolved from the crawl inventory rather than from the row, because the
     * workshop can be reached by a deep link (`?page=`) or by following an
     * internal link to a page that has no row in the current filter — in both
     * cases there is no row to read `issues` off.
     *
     * `undefined` (not []) when this run reported no inventory, or when it did
     * and this URL is not in it. Both mean "we did not measure this page", and
     * the panel says so rather than showing a clean bill of health.
     */
    // Keyed on `openUrl`, the raw URL param, rather than on `workshopPage` —
    // that is rebuilt as a fresh object on every render, so depending on it
    // would re-run this join every time anything on the screen changed.
    const workshopPageIssues = useMemo<AuditIssue[] | undefined>(() => {
        if (!openUrl) return undefined;
        const key = matchKey(openUrl);
        // Kept in `audit.issues` order, which is severity order — an error must
        // never sit below a notice just because of how the crawl keyed them.
        // Same guard as the table's join: a wire entry with no url must be
        // skipped, not fed to `matchKey`, which would throw mid-render.
        const sameUrl = (p: { url?: string }) =>
            typeof p?.url === "string" && p.url.trim() !== "" && matchKey(p.url) === key;
        const found = audit.issues.filter((i) => (i.pages ?? []).some(sameUrl));
        if (found.length > 0) return found;
        // Nothing found is two different facts. Only the inventory can tell them
        // apart: in it and failing nothing means the page passed; absent from it
        // (or no inventory at all) means this scan never read the page.
        const crawled = audit.crawledPages?.some(sameUrl);
        return crawled ? [] : undefined;
    }, [openUrl, audit.crawledPages, audit.issues]);

    // The workshop REPLACES the list rather than floating over it. At this
    // width an overlay was pretending to be a page; the work step is a step,
    // not an interruption, so it takes the surface and offers a way back.
    if (workshopPage) {
        return (
            <>
            <PageWorkshopView
                // Keyed by URL so a page switch starts the view fresh: its
                // in-flight publish state and any pending confirm belong to the page it was started on.
                key={workshopPage.url}
                url={workshopPage.url}
                title={workshopPage.title}
                onBack={() => setWorkshopPage(null)}
                onOpenPage={(url, title) => setWorkshopPage({ url, title })}
                roleOverride={roleOverrides[workshopPage.url]}
                review={reviews[workshopPage.url] ?? EMPTY_REVIEW}
                onReviewChange={(next) =>
                    setReviews((prev) => ({ ...prev, [workshopPage.url]: next }))
                }
                page={livePage !== undefined ? livePage : samplePage}
                loading={workshopLoading || generating}
                error={workshopError}
                // Wrapped so a click event can never arrive as the notes arg.
                onGenerate={businessId ? () => void generate() : undefined}
                onRedraft={businessId ? (notes) => void generate(notes) : undefined}
                // The live write (#4100). Only on a real business; the view
                // hides publishing entirely when this is absent.
                onPublish={businessId ? publish : samplePublish}
                // A builder-owned page refuses copy writes for its own reason,
                // and that reason is the publish endpoint's own sentence — so
                // the Workshop and the endpoint cannot describe the same page
                // differently. It takes precedence over the connector-level
                // reason: "Elementor owns this layout" is the more specific and
                // more actionable of the two, and it is the one the reviewer
                // can do something about.
                publishBlockedReason={
                    livePage?.publishable?.copy === false
                        ? livePage.publishable.detail ?? "This page's copy cannot be published from here."
                        : publishBlockedReason
                }
                publishTargetLabel={publishTargetLabel}
                publishUnsupported={publishUnsupported}
                // The sample's site is on Wix: no publish, the copy is pasted in. The client's yes is checked first.
                pasteOnly={businessId ? undefined : samplePasteOnly(business.id)}
                onPrecheck={
                    samplePublish && samplePage?.draftId
                        ? async (ids) => {
                              // The client's yes for these is in the Answer Plan already (agreed.ts).
                              // `ids` is the exact set being initiated, never the parent's staged state.
                              if (planAnswers && onlyPlan(ids)) return null;
                              const r = await samplePublish({ draftId: samplePage.draftId!, dismissed: [] });
                              return r.ok || r.recorded ? null : r;
                          }
                        : undefined
                }
                // Ask Oura (PREVIEW): the chat reports the copy as it went, so it never says "on your clipboard" when the browser refused.
                onPasteResult={
                    planAnswers
                        ? (copied) => {
                              const yes = pendingYes.current;
                              // A copy result from another page is not this yes's result: leave the
                              // current page's pending yes untouched so its own copy can still report it.
                              if (!yes || yes.pageUrl !== workshopPage.url) return;
                              pendingYes.current = null;
                              sayYesInChat(business.id, planAnswers, yes.actor, yes.count, { pasteOn: samplePasteOnly(business.id) ?? null, copied });
                          }
                        : undefined
                }
                onConfirmChanges={
                    planAnswers && samplePage
                        ? (ids) =>
                              onlyPlan(ids)
                                  ? new Promise<boolean>((resolve) => {
                                        const changes = samplePage.recommendations
                                            .filter((r) => ids.includes(r.id) && r.kind === "copy")
                                            .map((r) => (r.kind === "copy" ? { id: r.id, before: r.before, after: r.after } : null))
                                            .filter((c): c is YesChange => c !== null);
                                        setYesAsk({ pageUrl: workshopPage.url, changes, resolve });
                                    })
                                  : Promise.resolve(true)
                        : undefined
                }
                businessId={businessId ?? undefined}
                pageIssues={workshopPageIssues}
                // Document freshness (#3548): when we last read the client's
                // page, whether the last attempt failed, and a way to read it
                // again — a page edited in WordPress directly changes under us
                // with nothing to notice it.
                documentReadAt={documentReadAt[workshopPage.url]}
                documentStale={!!documentStale[workshopPage.url]}
                documentRefreshing={refreshingDocument === workshopPage.url}
                onRefreshDocument={
                    businessId ? () => void refreshDocument(workshopPage.url) : undefined
                }
                flashPlan={flashPlan}
            />
            {yesAsk && yesAsk.pageUrl === workshopPage.url && planAnswers && (
                <PlanYesDialog
                    changes={yesAsk.changes}
                    agreed={planAnswers}
                    name={signedIn}
                    pasteOn={samplePasteOnly(business.id) ?? null}
                    onCancel={() => {
                        yesAsk.resolve(false);
                        setYesAsk(null);
                    }}
                    onYes={() => {
                        const pasteOn = samplePasteOnly(business.id) ?? null;
                        // On a paste-only site the chat waits for the workshop's copy result (onPasteResult).
                        if (pasteOn) pendingYes.current = { pageUrl: yesAsk.pageUrl, actor: signedIn ?? "You", count: yesAsk.changes.length };
                        yesAsk.resolve(true);
                        setYesAsk(null);
                        if (!pasteOn) sayYesInChat(business.id, planAnswers, signedIn ?? "You", yesAsk.changes.length, { pasteOn: null, copied: false });
                    }}
                />
            )}
            </>
        );
    }

    return (
        <div>
            <Card className="overflow-hidden">
                <CardHeader
                    icon={FileTextIcon}
                    title="Crawled pages"
                    description={headerFacts.join(" · ")}
                />
                <CardBody className="p-0">
                    {/* The Search Console nudge: one line, attached to the table
                        it affects — not a banner floating above it.

                        Two different situations, and telling them apart is the
                        point. Nobody connected it: invite them to. Somebody
                        connected it and no row reached a crawled page: say
                        that, because "Connect Search Console" aimed at an
                        operator who already did reads as a broken product and
                        offers them nothing to do. */}
                    {!gscConnected && (
                        gscState && gscState.kind !== "not_connected" ? (
                            <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-amber-50/50 px-4 py-2.5">
                                <div className="min-w-0">
                                    <p className="my-0 text-sm text-gray-600">
                                        <span className="font-medium text-amber-700">{gscState.label}.</span>{" "}
                                        {gscState.detail} Until a row matches, these pages are ranked on crawl
                                        findings alone.
                                    </p>
                                    {/* What the last on-demand pull said, when it did
                                        not come back connected — fresher than the
                                        catalog line above until the re-fetch lands. */}
                                    {gscSyncNote && (
                                        <p
                                            className={cn(
                                                "my-0 mt-1 text-xs",
                                                gscSyncNote.tone === "ok" ? "text-emerald-700" : "text-amber-700",
                                            )}
                                        >
                                            {gscSyncNote.text}
                                        </p>
                                    )}
                                </div>
                                <span className="flex flex-shrink-0 items-center gap-3">
                                    {/* Re-read Search Console NOW, here, where the
                                        dashes are noticed — without the paid full
                                        re-scan that "Rerun assessment" triggers. */}
                                    {businessId && onRefreshData && (
                                        <button
                                            onClick={() => void refreshSearchConsole()}
                                            disabled={gscSyncing}
                                            className="text-sm font-medium text-blue-600 underline-offset-4 hover:underline disabled:opacity-50"
                                        >
                                            {gscSyncing ? "Refreshing…" : "Refresh data"}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => onNavigate("connectors")}
                                        className="text-sm font-medium text-blue-600 underline-offset-4 hover:underline"
                                    >
                                        Review connection
                                    </button>
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-blue-50/40 px-4 py-2.5">
                                <p className="my-0 text-sm text-gray-600">
                                    Connect your Google Search Console and Oura will prioritize these pages
                                    with real search data — which pages people find, and which they skip.
                                </p>
                                <button
                                    onClick={() => onNavigate("connectors")}
                                    className="flex-shrink-0 text-sm font-medium text-blue-600 underline-offset-4 hover:underline"
                                >
                                    Connect Search Console
                                </button>
                            </div>
                        )
                    )}

                    {/* Anything that qualifies how the table should be read: a
                        partial crawl, a link graph with nothing in it. Absent
                        on a healthy card. */}
                    <CrawlFindings notes={crawlFindings} />

                    {/* The controls, stacked: which pages, what that means, then
                        how to narrow them. Three rows rather than two — the ATS
                        board's own arrangement — because the description and a
                        search field on one line wrap into each other the moment
                        the description is longer than a few words.
                        `space-y-3` keeps them a single block against the table.

                        Pill toggle, NOT the underlined tab bar: this card sits
                        directly beneath the workspace's own underlined tabs
                        (Overview · Pages · Site Audit), and two underlined bars
                        stacked read as one confused navigation instead of a page
                        and a view inside it. The pills are quieter, which is
                        what a control nested inside a card should be.

                        No bottom rule here: the table below is its own bordered
                        surface now, and a full-bleed divider would draw a second
                        line a few pixels above the table's own top border. */}
                    <div className="space-y-3 px-4 py-3">
                        <SegmentedControl<TableView>
                            value={view}
                            onChange={setView}
                            options={VIEW_TABS.map((tab) => ({
                                value: tab.value,
                                label: tab.label,
                                count: viewCounts[tab.value],
                            }))}
                        />

                        <p className="my-0 text-sm leading-relaxed text-gray-700">
                            <span className="font-semibold text-gray-900">
                                {activeTab.label}:
                            </span>{" "}
                            {activeTab.description}
                            {/* A fixture business has no crawl behind it, and
                                that has to be said where the claim above is
                                made, not in a footnote under the table. */}
                            {!businessId && " This is sample data."}
                        </p>

                        <div className="flex items-center gap-3">
                            <div className="relative min-w-0 flex-1">
                                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <Input
                                    className="pl-9"
                                    placeholder="Search page or address…"
                                    aria-label="Search pages"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </div>
                            <Select
                                value={roleFilter}
                                onValueChange={(v) => setRoleFilter(v as "all" | PageRole)}
                            >
                                <SelectTrigger
                                    aria-label="Filter by role"
                                    className="w-[170px] flex-shrink-0 text-sm"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                {/* Same explanations as the per-row control. A
                                    reviewer meets the vocabulary in whichever
                                    of the two they open first, and "Utility"
                                    is exactly as opaque here as it is there. */}
                                <SelectContent className="max-h-[36rem] max-w-[340px]">
                                    <SelectGroup>
                                        <SelectItem value="all">
                                            All Roles{" "}
                                            <span className="text-gray-400">({inView.length})</span>
                                        </SelectItem>
                                        {COPY_WORK_ROLES.map((role) => {
                                            const count = roleCounts[role] ?? 0;
                                            // Hide-when-0 (§6a): the menu lists the
                                            // roles this crawl actually contains,
                                            // not the enum.
                                            if (count === 0) return null;
                                            return (
                                                <SelectItem
                                                    key={role}
                                                    value={role}
                                                    description={ROLE_HINT[role]}
                                                >
                                                    {ROLE_LABEL[role]}{" "}
                                                    <span className="text-gray-400">({count})</span>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectGroup>
                                    {/* "Out of scope" is the table's own third tab,
                                        word for word. This heading first read
                                        "Set aside · no copy work", which named
                                        the same thing a second way — and the
                                        page a reviewer lands on after choosing
                                        one of these is the tab, so the two have
                                        to agree or the label looks like it did
                                        something else.

                                        The whole group is dropped when this crawl
                                        has none — a heading over an empty list
                                        reads as a filter that lost its options. */}
                                    {asideRolesInView.length > 0 && (
                                        <>
                                            <SelectSeparator />
                                            <SelectGroup>
                                                <SelectLabel className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                    Out of scope
                                                </SelectLabel>
                                                {asideRolesInView.map((role) => (
                                                    <SelectItem
                                                        key={role}
                                                        value={role}
                                                        description={ROLE_HINT[role]}
                                                    >
                                                        {ROLE_LABEL[role]}{" "}
                                                        <span className="text-gray-400">
                                                            ({roleCounts[role] ?? 0})
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectGroup>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* The table is its own SURFACE: bordered, rounded, inset
                        from the card's edges — not a band running wall to wall.
                        The card is a container of several things (a header, a
                        findings notice, controls, a table), and the one that is
                        a grid of data should look like an object sitting in it.

                        `overflow-hidden` is what makes the radius real: it clips
                        the grey header band's top corners, the last row's
                        background, and the horizontal scrollbar a narrow viewport
                        produces — all three of which otherwise paint square right
                        over the curve.

                        Everything that belongs to the table lives INSIDE it: the
                        rows, the empty state that replaces them, and the pager
                        that walks them. That is what makes the bottom edge
                        curve whichever of the three happens to be last.

                        No per-tab explainer band above it, either. Each row
                        states its own case in two columns the reader is already
                        looking at — Role says Product or Placeholder, Why
                        flagged says "Not copy work" — so a paragraph here
                        repeated in bulk what the rows were about to say
                        individually. The empty state carries the explanation for
                        the one case where no row can. */}
                    <div className="mx-4 mb-4 overflow-hidden rounded-lg border border-gray-200">
                        <div className="overflow-x-auto">
                            {/* `w-full` and NOT `min-w-max`.
                                
                                `min-w-max` used to sit here to force the horizontal
                                scrollbar on a narrow viewport instead of letting the
                                columns squash. It is not needed: the metric columns
                                are `whitespace-nowrap`, the identity block carries its
                                own max-width and "Issues" has a floor, so the
                                table still refuses to compress below ~1150px and still
                                scrolls (measured at a 900px viewport).
                                
                                It is also actively harmful next to the greedy action
                                column below. `width: 100%` on a cell inside a
                                max-content-sized table is a contradiction, and the
                                browser resolves it by making the table as wide as it
                                possibly can — measured at 500,000px, which is what a
                                "huge gap after the last column" turns out to be. */}
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                        {/* Sorting by Page restores site order —
                                            home, then top-level pages, then their
                                            children — which is the arrangement that
                                            reads as a map of the site. It is the way
                                            back from a metric sort. */}
                                        {/* A CEILING, not a floor.
                                            
                                            The table is `w-full` with automatic
                                            layout, so some column absorbs the spare
                                            width, and with nothing capped it was
                                            this one: the page name wraps, so it
                                            reports a large content width and takes
                                            the slack. The trailing View column was
                                            then pushed past the right edge and
                                            could only be reached by scrolling.
                                            
                                            360px fits the longest names measured
                                            over two lines and leaves the rest of the
                                            row — four metric columns, Role, the
                                            240px Issues floor and the action — inside
                                            a 1024px content column, which is a
                                            1280px screen minus the sidebar.
                                            
                                            The old 400px cap sat on the identity
                                            BLOCK while the cell around it stretched,
                                            and the difference between the two was
                                            the dead gap in front of Clicks. Capping
                                            the CELL and letting the block fill it
                                            (`w-full` below) is the same ceiling
                                            without that gap. */}
                                        <SortHeader
                                            label="Page"
                                            sortKey="site"
                                            sort={effectiveSort}
                                            onSort={onSort}
                                            align="left"
                                            thClassName="w-[360px] max-w-[360px]"
                                        />
                                        {/* The metric columns are ALWAYS present:
                                            the reviewer works from search numbers
                                            here instead of round-tripping to the
                                            Search Console UI, and before the
                                            connector exists the headers say what
                                            connecting buys (the nudge line above
                                            says how). */}
                                        <SortHeader label="Clicks" sortKey="clicks" sort={effectiveSort} onSort={onSort} />
                                        <SortHeader label="Impressions" sortKey="impressions" sort={effectiveSort} onSort={onSort} />
                                        <SortHeader label="CTR" sortKey="ctr" sort={effectiveSort} onSort={onSort} />
                                        <SortHeader label="Position" sortKey="position" sort={effectiveSort} onSort={onSort} />
                                        {/* "Links in" is WITHHELD, not missing.
                                            The numbers are correct per row, but the
                                            crawl sampled the wrong 30 pages
                                            (ourteam-backend#3957): the pages that
                                            receive the site's internal links were
                                            never fetched, so almost every row reads
                                            "Orphan". Accurate per row, false about
                                            the site — and not something to put in
                                            front of a client. The join stays in
                                            `rows` so this is one line to restore
                                            once the crawl selection is fixed. */}
                                        <th className={`${TH} text-center`}>Role</th>
                                        {/* A floor so the findings column, the only
                                            one left whose text wraps, is not the one
                                            the browser squeezes: without it a wide
                                            viewport broke every finding onto two
                                            lines. 240px is the widest label measured
                                            ("Missing meta description", 145px) plus
                                            its icon and the cell padding. */}
                                        <th className={`${TH} min-w-[240px] text-left`}>Issues</th>
                                        {/* NO greedy `w-full` column here, and none
                                            anywhere in this table. Making one column
                                            absorb the spare width does not remove the
                                            gap, it relocates it — first into a
                                            labelled column, where "Issues" sat
                                            above a screen's width of white, then into
                                            this unnamed one, where roughly 800px
                                            opened up in front of the View link. The
                                            gap was never about which column held the
                                            slack; it was that the page-name BLOCK was
                                            capped smaller than the cell it sat in.
                                            Fixed there (see PageIdentity below), so
                                            every column can size normally. */}
                                        {/* Pinned to the right edge, so the one action
                                            a row has stays in view whenever the table
                                            is wider than its card and scrolls. */}
                                        <th className={cn(TH, STICKY_ACTION, "bg-gray-50")}>
                                            <span className="sr-only">Action</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {visibleRows.slice(offset, offset + PAGE_SIZE).map((r) => (
                                        // Cells sit MIDDLE by default and only the two that
                                        // can run to several lines opt into top alignment (the
                                        // page identity and the findings list). Top-aligning the
                                        // whole row pinned a single centred number, a select
                                        // control and a one-word action to the top edge of a
                                        // tall row, which reads as the row having been cut off
                                        // rather than as anything lining up.
                                        <tr
                                            key={r.url}
                                            // The whole row opens the page's workshop, where View does.
                                            onClick={workshopEnabled && r.finding !== "404" && roleOf(r.url) !== "placeholder" ? rowClick(() => setWorkshopPage({ url: r.url, title: r.title ?? r.url })) : undefined}
                                            className={cn("group/row transition-colors hover:bg-gray-50", workshopEnabled && r.finding !== "404" && roleOf(r.url) !== "placeholder" && "cursor-pointer")}
                                        >
                                            {/* Matches the header's ceiling. Both
                                                are needed: the cap on one row alone
                                                does not constrain a column. */}
                                            <td className="w-[360px] max-w-[360px] px-4 py-4 align-top">
                                                {/* First column = identity icon +
                                                    primary text + subtext (§4). The
                                                    glyph tells the reader what a row
                                                    IS at a glance — the same tile
                                                    every other table in the product
                                                    leads with, one size down from
                                                    the StatTile family. */}
                                                {/* `items-start`: the name wraps now, so
                                                    centring pushed the glyph to the
                                                    middle of a two-line block instead of
                                                    lining it up with the first line. */}
                                                {/* `min-w-0`: without it this flex row
                                                    cannot shrink below its content, so the
                                                    truncating path line inside PageIdentity
                                                    reported its FULL width as the column's
                                                    minimum and pushed the table into a
                                                    horizontal scroll. Only pages carrying a
                                                    long path showed it, which is why it
                                                    appeared on one page of the table and not
                                                    its neighbours. */}
                                                <div className="flex min-w-0 items-start gap-3">
                                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                                                        <FileTextIcon className="h-4 w-4 text-blue-600" />
                                                    </div>
                                                    {/* `w-full`, NOT a fixed max-width.
                                                        This is what closed the gap
                                                        between a page name and its own
                                                        clicks figure. The block used to
                                                        be capped at 400px inside a
                                                        column the table had stretched
                                                        past it, and the difference was
                                                        the gap: dead space belonging to
                                                        the name's cell that the name was
                                                        forbidden to use. Filling the
                                                        column means the text runs to the
                                                        cell padding and stops — 16px
                                                        from the next column, measured,
                                                        at every viewport width. */}
                                                    <PageIdentity
                                                        title={r.title}
                                                        url={r.url}
                                                        className="w-full"
                                                        nameSuffix={
                                                            r.chosen ? <ChosenBadge /> : undefined
                                                        }
                                                    />
                                                </div>
                                            </td>
                                            <MetricCell empty={r.clicks === null}>{fmt(r.clicks)}</MetricCell>
                                            <MetricCell empty={r.impressions === null}>{fmt(r.impressions)}</MetricCell>
                                            <MetricCell empty={r.ctr === null}>
                                                {r.ctr === null ? null : `${ctrPercent(r.ctr).toFixed(1)}%`}
                                            </MetricCell>
                                            <MetricCell empty={r.position === null}>
                                            {r.position === null ? null : (
                                                <span title={`Average position ${r.position}`}>
                                                    {positionLabel(r.position)}
                                                </span>
                                            )}
                                        </MetricCell>
                                            <td className="whitespace-nowrap px-4 py-4">
                                                {/* Editable because the inference is
                                                    a URL guess. House Select (§6a),
                                                    centered under its header. */}
                                                <div className="flex justify-center">
                                                    <Select
                                                        value={roleOf(r.url)}
                                                        onValueChange={(v) => setRole(r.url, v as PageRole)}
                                                    >
                                                        <SelectTrigger
                                                            aria-label={`Role for ${r.url}`}
                                                            className="w-[132px] flex-shrink-0 text-sm"
                                                        >
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        {/* Wider than the trigger on purpose:
                                                            the trigger holds one word, the menu
                                                            holds the explanation of nine. The
                                                            height cap is raised past the shared
                                                            default too — nine two-line options
                                                            overflow 24rem, and a menu that
                                                            scrolls hides the very roles a
                                                            reviewer does not recognise, which
                                                            are the ones at the bottom. */}
                                                        <SelectContent className="max-h-[36rem] max-w-[340px]">
                                                            {/* Two GROUPS, not a flat list with a
                                                                heading in the middle. Radix ties
                                                                a SelectLabel to its SelectGroup
                                                                for `aria-labelledby` and THROWS
                                                                without one, so the grouping is
                                                                what makes the heading legal as
                                                                well as what makes it meaningful:
                                                                a screen reader announces "Set
                                                                aside" as the group these three
                                                                belong to, rather than reading a
                                                                stray line of text. */}
                                                            <SelectGroup>
                                                                {COPY_WORK_ROLES.map((role) => (
                                                                    <SelectItem
                                                                        key={role}
                                                                        value={role}
                                                                        description={ROLE_HINT[role]}
                                                                    >
                                                                        {ROLE_LABEL[role]}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectGroup>
                                                            <SelectSeparator />
                                                            <SelectGroup>
                                                                {/* Choosing one of these three takes
                                                                    the page out of Oura's picks.
                                                                    That is the whole reason they
                                                                    exist and it was nowhere on
                                                                    screen — the menu listed them as
                                                                    if they were three more kinds of
                                                                    page.

                                                                    Named after the tab the page
                                                                    moves to, exactly: pick one here
                                                                    and the row leaves this list for
                                                                    "Out of scope". A heading that
                                                                    said it a different way would
                                                                    make one action look like two. */}
                                                                <SelectLabel className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                                    Out of scope
                                                                </SelectLabel>
                                                                {SET_ASIDE_ROLES.map((role) => (
                                                                    <SelectItem
                                                                        key={role}
                                                                        value={role}
                                                                        description={ROLE_HINT[role]}
                                                                    >
                                                                        {ROLE_LABEL[role]}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectGroup>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </td>
                                            {/* Middle-aligned in both states, and the two
                                                cases agree. Collapsed, the cell is a single
                                                line and sits level with the row's numbers.
                                                Expanded, the open list is usually the tallest
                                                thing in the row, so it sets the row's height
                                                and middle lands exactly where top would.
                                                A lone "—" additionally centres across, so it
                                                reads as the absence it is rather than as a
                                                value that failed to load. */}
                                            <td
                                                className={cn(
                                                    "px-4 py-4 align-middle",
                                                    hasNoFindings(r, roleOf(r.url)) && "text-center",
                                                )}
                                            >
                                                <IssuesCell row={r} role={roleOf(r.url)} />
                                            </td>
                                            <td className={cn("whitespace-nowrap px-4 py-4 text-right transition-colors group-hover/row:bg-gray-50", STICKY_ACTION, "bg-white")}>
                                                {/* A 404 has no copy to improve — its fix
                                                    is a redirect, owned by Site Audit.

                                                    "View" alone, not "View page details":
                                                    it sits in the last column of a row that
                                                    is entirely about one page, so "page
                                                    details" restated what the other six
                                                    columns had already established and
                                                    charged the widest column in the table
                                                    for it. The width goes to the page name,
                                                    which is the part that was truncating.

                                                    Middle-aligned: one word has no reading
                                                    order to preserve, so it sits level with the
                                                    row's numbers rather than with the first line
                                                    of a title it is not attached to. */}
                                                {/* The one action a placeholder page
                                                    actually needs (#4529 §3). Its fix
                                                    is not copy — the Role select already
                                                    says "Should be removed" — and until
                                                    now the product said that and offered
                                                    no way to do it. Only for placeholders:
                                                    the backend refuses anything else, and
                                                    a button that is usually refused is a
                                                    button nobody trusts. */}
                                                {businessId && roleOf(r.url) === "placeholder" && (
                                                    removed.has(r.url) ? (
                                                        <span className="inline-flex items-center gap-1 text-sm text-gray-400">
                                                            <EyeOffIcon className="h-3.5 w-3.5" />
                                                            Off the site
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => {
                                                                setRemoveError(null);
                                                                setRemoving({ url: r.url, title: r.title ?? r.url });
                                                            }}
                                                            className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 underline-offset-4 hover:text-gray-900 hover:underline"
                                                        >
                                                            <EyeOffIcon className="h-3.5 w-3.5" />
                                                            Take off the site
                                                        </button>
                                                    )
                                                )}
                                                {r.finding !== "404" &&
                                                    roleOf(r.url) !== "placeholder" &&
                                                    (workshopEnabled ? (
                                                        <button
                                                            onClick={() =>
                                                                setWorkshopPage({
                                                                    url: r.url,
                                                                    title: r.title ?? r.url,
                                                                })
                                                            }
                                                            className="group inline-flex items-center gap-1 text-sm font-medium text-blue-600 underline-offset-4 hover:underline"
                                                        >
                                                            View
                                                            {/* The arrow moves on hover, which is
                                                                the affordance doing the work an
                                                                underline alone cannot: it says the
                                                                click LEAVES this table for the
                                                                page's own workshop, rather than
                                                                opening something over it. */}
                                                            <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                                        </button>
                                                    ) : (
                                                        // Shown, not hidden: the operator should know
                                                        // the step exists rather than wonder why some
                                                        // rows have no action. Unreachable today —
                                                        // `workshopEnabled` is true everywhere — but
                                                        // kept so turning the gate off degrades to a
                                                        // disabled action instead of a missing column.
                                                        <span
                                                            className="inline-flex cursor-not-allowed items-center gap-1 text-sm text-gray-300"
                                                            title="The page workshop is switched off for this business"
                                                        >
                                                            View
                                                            <ArrowRightIcon className="h-3.5 w-3.5" />
                                                        </span>
                                                    ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Two different nothings, and they need different answers
                            (house style §9). A tab with nothing in it is a fact
                            about the site — nothing has been ruled out of scope yet
                            — and offers nothing to undo. A filter with no matches is
                            something the reviewer did, so it offers the way back.
                            This is also where the Out of scope tab explains itself:
                            the one case where no row is present to do it. */}
                        {visibleRows.length === 0 &&
                            (filtersApplied ? (
                                <EmptyState
                                    icon={SearchIcon}
                                    title="No pages match these filters"
                                    description="Nothing in this tab matches the search and role you have set."
                                    action={
                                        <button
                                            onClick={clearFilters}
                                            className="text-sm font-medium text-blue-600 underline-offset-4 hover:underline"
                                        >
                                            Clear filters
                                        </button>
                                    }
                                />
                            ) : (
                                <EmptyState
                                    icon={FileTextIcon}
                                    title={
                                        rows.length === 0
                                            ? "No pages in this crawl"
                                            : view === "aside"
                                                ? "Nothing ruled out of scope yet"
                                                : "No pages to work on"
                                    }
                                    description={
                                        rows.length === 0
                                            ? "Oura has not recorded any pages for this site yet. Rerun the assessment if the crawl looks incomplete."
                                            : view === "aside"
                                                ? "Set a page's role to Product, Utility or Placeholder and it lands here: product and utility pages need no copy proposals, placeholder pages should be removed from the site. Oura stops picking any of them."
                                                : "Every page in this crawl is out of scope. Check the Out of scope tab."
                                    }
                                />
                            ))}
                        {visibleRows.length > PAGE_SIZE && (
                            <div className="border-t border-gray-200 px-4 py-2.5">
                                <Pagination
                                    page={page}
                                    total={visibleRows.length}
                                    pageSize={PAGE_SIZE}
                                    itemLabel="pages"
                                    onPageChange={setPage}
                                />
                            </div>
                        )}
                    </div>
                    {/* No footnote. Everything it used to carry — the read-only
                        promise, the browser-only caveat, the sample-data note —
                        now sits in the description of the tab it is about, at
                        the top of the card where it is read before the table
                        rather than after it. */}
                </CardBody>
            </Card>

            {removing && (
                <RemovePageDialog
                    url={removing.url}
                    title={removing.title}
                    busy={removeBusy}
                    error={removeError}
                    onConfirm={() => void confirmRemove()}
                    onClose={() => setRemoving(null)}
                />
            )}
        </div>
    );
}
